import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  BarChart3,
  Box,
  Building2,
  ChevronDown,
  Clock3,
  Coins,
  Filter,
  ExternalLink,
  Layers3,
  ListTodo,
  MapPin,
  Package,
  Percent,
  Plus,
  Receipt,
  RefreshCw,
  Sparkles,
  Target,
  TrendingUp,
  X,
  UserCheck,
  UserRound,
  Users,
  WalletCards,
} from 'lucide-react';

import DatePicker from '../components/DatePicker';
import Dropdown from '../components/Dropdown';
import { UsersListSkeleton } from '../components/Skeleton';
import ModalShell from '../components/ModalShell';
import useToast from '../hooks/useToast';
import useModalBackButton from '../hooks/useModalBackButton';
import {
  getCurrentAuthUser,
  getDiscountStatistics,
  getEmployeeStatistics,
  getEmployeeOrderDetails,
  getEmployeeStageDetails,
  getGeneralAdditionalServiceDetails,
  getGeneralOrderDetails,
  getGeneralStageDetails,
  getGeneralStatistics,
  getSourceStatistics,
  getStatisticsUsers,
} from '../services/statisticsApi';
import {
  STAGE_BADGE_STYLES,
  STAGE_BG_STYLES,
  USER_STAGE_ORDER,
  getStageConfig,
} from '../utils/stages';
import { formatPersonName } from '../utils/personName';
import { haptic } from '../utils/vibration';
import { shouldUseOfflineDetailFallback } from '../utils/offlineRoutesCache';

const ADMIN_TABS = [
  { id: 'employee', label: 'По сотруднику', icon: UserRound },
  { id: 'general', label: 'Общая', icon: BarChart3 },
  { id: 'discounts', label: 'Скидки', icon: Percent },
  { id: 'sources', label: 'Источники', icon: Target },
];

const EMPLOYEE_TABS = [{ id: 'employee', label: 'Моя статистика', icon: UserRound }];

const STATISTICS_SECTION_TITLES = {
  employee: 'Статистика пользователя',
  general: 'Общая',
  discounts: 'По скидкам',
  sources: 'По источникам',
};

const SURFACE_TONES = {
  blue: {
    card: 'from-cyan-500 to-blue-600',
    text: 'text-cyan-700 dark:text-cyan-300',
    soft: 'bg-cyan-50 dark:bg-cyan-950/30 border-cyan-200 dark:border-cyan-900/60',
    bar: 'bg-cyan-500',
  },
  emerald: {
    card: 'from-emerald-500 to-teal-600',
    text: 'text-emerald-700 dark:text-emerald-300',
    soft: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900/60',
    bar: 'bg-emerald-500',
  },
  violet: {
    card: 'from-violet-500 to-fuchsia-600',
    text: 'text-violet-700 dark:text-violet-300',
    soft: 'bg-violet-50 dark:bg-violet-950/30 border-violet-200 dark:border-violet-900/60',
    bar: 'bg-violet-500',
  },
  amber: {
    card: 'from-amber-500 to-orange-600',
    text: 'text-amber-700 dark:text-amber-300',
    soft: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900/60',
    bar: 'bg-amber-500',
  },
  rose: {
    card: 'from-rose-500 to-pink-600',
    text: 'text-rose-700 dark:text-rose-300',
    soft: 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-900/60',
    bar: 'bg-rose-500',
  },
};

const toInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const MAX_STATISTICS_DAYS = 92;

const getPeriodDays = (dateFrom, dateTo) => {
  if (!dateFrom || !dateTo) return 0;
  const from = new Date(`${dateFrom}T00:00:00`);
  const to = new Date(`${dateTo}T00:00:00`);
  const diffTime = to.getTime() - from.getTime();
  if (Number.isNaN(diffTime)) return 0;
  if (diffTime < 0) return -1;
  return Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
};

const getCurrentMonthRange = () => {
  const now = new Date();
  return {
    date_from: toInputDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    date_to: toInputDate(now),
  };
};

const getLastDaysRange = (days) => {
  const now = new Date();
  const from = new Date(now);
  from.setDate(now.getDate() - (days - 1));
  return {
    date_from: toInputDate(from),
    date_to: toInputDate(now),
  };
};

const formatMoney = (value) =>
  `${Number(value || 0).toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} ₽`;

const formatMetric = (value) => {
  const numeric = Number(value || 0);
  if (Number.isInteger(numeric)) return numeric.toLocaleString('ru-RU');
  return numeric.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const formatPaymentMethod = (value) => {
  const method = String(value || '').trim().toLowerCase();
  if (method === 'cash') return 'Наличные';
  if (method === 'settlement_account') return 'Расчётный счёт';
  if (['card', 'online', 'transfer'].includes(method)) return 'Безнал';
  return value || 'Не указан';
};

const formatFilterDate = (value) => {
  if (!value) return 'Не выбрано';
  const [year, month, day] = String(value).split('-');
  if (!year || !month || !day) return value;
  return `${day}.${month}.${year}`;
};

const formatFilterRange = (dateFrom, dateTo) => `${formatFilterDate(dateFrom)} - ${formatFilterDate(dateTo)}`;

const formatStatisticsUserLabel = (user, fallback = 'Пользователь') => (
  formatPersonName(user, user?.name || user?.login || (user?.id ? `Пользователь #${user.id}` : fallback))
);

const formatStageValue = (stage, row) => {
  if (!row) return '0';
  if (stage === 'delivery' || stage === 'pickup') return `${formatMetric(row.orders_count)} зак.`;
  return `${formatMetric(row.items_count)} предм.`;
};

const areFiltersEqual = (left, right) => (
  left.date_from === right.date_from
  && left.date_to === right.date_to
  && `${left.stage || ''}` === `${right.stage || ''}`
  && `${left.user_id || ''}` === `${right.user_id || ''}`
);

const getScopeHint = (tab, stage) => {
  if (!stage || !['discounts', 'sources'].includes(tab)) return '';
  return 'Этап здесь фильтрует заказы, которые дошли до выбранного этапа за период.';
};

const SectionCard = ({
  title,
  icon: Icon,
  tone = 'blue',
  children,
  actions = null,
  collapsible = false,
  defaultOpen = false,
}) => {
  const toneMeta = SURFACE_TONES[tone] || SURFACE_TONES.blue;
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const toggleOpen = () => {
    if (!collapsible) return;
    haptic('tap');
    setIsOpen((prev) => !prev);
  };
  return (
    <section className={`rounded-2xl border bg-white shadow-sm dark:bg-gray-900 ${toneMeta.soft}`}>
      <div
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? isOpen : undefined}
        onClick={toggleOpen}
        onKeyDown={(event) => {
          if (!collapsible || !['Enter', ' '].includes(event.key)) return;
          event.preventDefault();
          toggleOpen();
        }}
        className={`flex items-center justify-between gap-3 border-b border-black/5 px-4 py-3 dark:border-white/5 ${collapsible ? 'cursor-pointer select-none transition-colors hover:bg-white/45 dark:hover:bg-white/[0.03]' : ''}`}
      >
        <div className="flex min-w-0 items-center gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br text-white shadow-sm ${toneMeta.card}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="truncate text-sm font-semibold text-gray-950 dark:text-white">{title}</div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {actions ? <div onClick={(event) => event.stopPropagation()}>{actions}</div> : null}
          {collapsible ? (
            <div
              aria-hidden="true"
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-black/5 bg-white/70 text-gray-500 transition-colors hover:bg-white dark:border-white/10 dark:bg-gray-900/60 dark:text-gray-300 dark:hover:bg-gray-900"
            >
              <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>
          ) : null}
        </div>
      </div>
      <AnimatePresence initial={false}>
        {(!collapsible || isOpen) ? (
          <motion.div
            initial={collapsible ? { opacity: 0, height: 0 } : false}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="p-4">{children}</div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </section>
  );
};

const SummaryCard = ({ label, value, note, icon: Icon, tone = 'blue', onClick = null }) => {
  const toneMeta = SURFACE_TONES[tone] || SURFACE_TONES.blue;
  const isClickable = typeof onClick === 'function';
  const CardElement = isClickable ? motion.button : motion.div;
  return (
    <CardElement
      type={isClickable ? 'button' : undefined}
      onClick={isClickable ? () => {
        haptic('tap');
        onClick();
      } : undefined}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className={`w-full rounded-2xl border border-gray-200 bg-white p-4 text-left shadow-sm dark:border-gray-800 dark:bg-gray-900 ${isClickable ? 'transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/80' : ''}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">{label}</div>
          <div className="mt-2 text-2xl font-semibold tracking-tight text-gray-950 dark:text-white">{value}</div>
          {note ? <div className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">{note}</div> : null}
        </div>
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br text-white ${toneMeta.card}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </CardElement>
  );
};

const TabButton = ({ active, label, icon: Icon, onClick, iconOnly = false }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
      active
        ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-500 dark:bg-blue-900/30 dark:text-blue-200'
        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
    } ${iconOnly ? 'h-10 w-10 justify-center px-0 lg:h-auto lg:w-auto lg:justify-start lg:px-4 lg:py-2' : ''}`}
  >
    <Icon className="h-4 w-4 shrink-0" />
    {iconOnly ? <span className="hidden lg:inline">{label}</span> : <span>{label}</span>}
  </button>
);

const QuickRangeButton = ({ active, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-full px-3 py-2 text-xs font-medium transition-colors lg:rounded-xl lg:px-4 lg:py-2 lg:text-sm ${
      active
        ? 'bg-blue-600 text-white'
        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
    }`}
  >
    {label}
  </button>
);

const StatisticsSkeleton = ({ isLongPeriod = false, periodDays = 0 }) => (
  <div className="space-y-4">
    {isLongPeriod ? (
      <div className="flex items-center gap-2.5 rounded-2xl border border-blue-200 bg-blue-50/90 px-4 py-3 text-xs text-blue-800 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-200">
        <Clock3 className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
        <span>
          Формирование статистики за большой период ({periodDays} дн.) может занять чуть больше времени. Пожалуйста, подождите...
        </span>
      </div>
    ) : null}
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      {[...Array(4)].map((_, index) => (
        <div key={index} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="h-3 w-24 animate-pulse rounded bg-gray-200 dark:bg-gray-800" />
              <div className="mt-3 h-8 w-28 animate-pulse rounded bg-gray-200 dark:bg-gray-800" />
              <div className="mt-2 h-3 w-20 animate-pulse rounded bg-gray-100 dark:bg-gray-800/70" />
            </div>
            <div className="h-11 w-11 animate-pulse rounded-2xl bg-gray-200 dark:bg-gray-800" />
          </div>
        </div>
      ))}
    </div>
    <div className="space-y-4">
      {[...Array(2)].map((_, index) => (
        <div key={index} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <UsersListSkeleton count={3} />
        </div>
      ))}
    </div>
  </div>
);

const DataEmptyState = ({ title, description }) => (
  <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-10 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-300">
      <BarChart3 className="h-5 w-5" />
    </div>
    <div className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</div>
    <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</div>
  </div>
);

const StatisticsErrorState = ({ title, description, onRetry, retryDisabled = false }) => (
  <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-center shadow-sm dark:border-amber-900 dark:bg-amber-950/30">
    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
      <AlertTriangle className="h-6 w-6" />
    </div>
    <h3 className="mt-4 text-base font-semibold text-amber-950 dark:text-amber-100">{title}</h3>
    <p className="mx-auto mt-2 max-w-md text-sm leading-5 text-amber-800 dark:text-amber-200/85">{description}</p>
    <button
      type="button"
      onClick={onRetry}
      disabled={retryDisabled}
      className="mt-4 inline-flex min-h-[40px] items-center justify-center gap-2 rounded-xl bg-amber-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
    >
      <RefreshCw className="h-4 w-4" />
      Обновить
    </button>
  </section>
);

const HorizontalBars = ({ rows, tone = 'blue', valueKey = 'count', subtitle, onRowClick = null }) => {
  const toneMeta = SURFACE_TONES[tone] || SURFACE_TONES.blue;
  const maxValue = Math.max(1, ...rows.map((row) => Number(row[valueKey] || 0)));
  const isClickable = typeof onRowClick === 'function';

  return (
    <div className="space-y-3">
      {subtitle ? <div className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</div> : null}
      {rows.map((row) => {
        const RowElement = isClickable ? 'button' : 'div';
        return (
        <RowElement
          key={`${row.name || row.source}-${row[valueKey]}`}
          type={isClickable ? 'button' : undefined}
          onClick={isClickable ? () => {
            haptic('tap');
            onRowClick(row);
          } : undefined}
          className={`w-full rounded-2xl bg-gray-50 px-3 py-3 text-left dark:bg-gray-950/40 ${isClickable ? 'transition-colors hover:bg-gray-100 dark:hover:bg-gray-900' : ''}`}
        >
          <div className="flex items-center justify-between gap-3 text-sm">
            <div className="min-w-0 truncate font-medium text-gray-900 dark:text-gray-100">{row.name || row.source}</div>
            <div className={`shrink-0 font-semibold ${toneMeta.text}`}>{formatMetric(row[valueKey])}</div>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.max(6, (Number(row[valueKey] || 0) / maxValue) * 100)}%` }}
              transition={{ duration: 0.45, ease: 'easeOut' }}
              className={`h-full rounded-full ${toneMeta.bar}`}
            />
          </div>
          {row.note ? <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">{row.note}</div> : null}
        </RowElement>
        );
      })}
    </div>
  );
};

const DepartmentBreakdown = ({
  rows = [],
  mode = 'general',
  onRowClick = null,
  title = null,
  subtitle: customSubtitle = null,
  defaultOpen = false,
  icon = null,
  tone = null,
}) => {
  const [expandedDeptKeys, setExpandedDeptKeys] = useState(() => new Set());

  const toggleDept = (deptKey) => {
    setExpandedDeptKeys((prev) => {
      const next = new Set(prev);
      if (next.has(deptKey)) {
        next.delete(deptKey);
      } else {
        next.add(deptKey);
      }
      return next;
    });
  };

  const viewRows = rows.map((item) => {
    const deptKey = item.department_id !== null && item.department_id !== undefined ? String(item.department_id) : 'none';
    const localities = Array.isArray(item.localities) ? item.localities : [];
    if (mode === 'discounts') {
      const sortedLocalities = [...localities].sort((a, b) => {
        const discA = Number(a.discounted_orders_count ?? 0);
        const discB = Number(b.discounted_orders_count ?? 0);
        if (discB !== discA) return discB - discA;
        const totalDiscA = Number(a.discounts_total ?? 0);
        const totalDiscB = Number(b.discounts_total ?? 0);
        if (totalDiscB !== totalDiscA) return totalDiscB - totalDiscA;
        return Number(b.orders_count || 0) - Number(a.orders_count || 0);
      });
      return {
        raw: item,
        deptKey,
        department_id: item.department_id,
        name: item.department_name,
        count: item.discounted_orders_count,
        localities: sortedLocalities,
        note: `${formatMetric(item.orders_count)} зак. в срезе • скидки ${formatMoney(item.discounts_total)} • средняя ${formatMoney(item.average_discount)}`,
      };
    }
    if (mode === 'employee' || mode === 'created') {
      return {
        raw: item,
        deptKey,
        department_id: item.department_id,
        name: item.department_name,
        count: item.orders_count,
        localities,
        note: `${formatMetric(item.share_percent)}% созданных заказов • ${formatMetric(item.positions_count)} поз. • сумма ${formatMoney(item.revenue_total)}`,
      };
    }
    if (mode === 'sources') {
      const sources = Array.isArray(item.sources) ? item.sources : [];
      return {
        raw: item,
        deptKey,
        department_id: item.department_id,
        name: item.department_name,
        count: item.orders_count,
        localities,
        sources,
        note: `${formatMetric(item.share_percent)}% заказов • ${formatMetric(item.positions_count)} поз. • выручка ${formatMoney(item.revenue_total)} • средний чек ${formatMoney(item.average_check)}`,
      };
    }
    return {
      raw: item,
      deptKey,
      department_id: item.department_id,
      name: item.department_name,
      count: item.orders_count,
      localities,
      note: `${formatMetric(item.share_percent)}% заказов • ${formatMetric(item.positions_count)} поз. • выручка ${formatMoney(item.revenue_total)} • средний чек ${formatMoney(item.average_check)} • скидки ${formatMoney(item.discounts_total)}`,
    };
  });

  const subtitle = customSubtitle || (
    mode === 'created'
      ? 'Заказы, созданные за выбранный период, распределены по подразделениям. Нажмите на подразделение, чтобы раскрыть населённые пункты.'
      : mode === 'employee'
        ? 'Заказы, созданные выбранным пользователем, распределены по подразделениям заказа.'
        : mode === 'discounts'
          ? 'Полоса показывает количество заказов со скидкой в каждом подразделении.'
          : mode === 'sources'
            ? 'Тот же срез источников распределён по подразделениям и населённым пунктам. Нажмите на подразделение, чтобы раскрыть населённые пункты.'
            : 'Завершённые заказы текущего среза распределены по подразделениям. Нажмите на подразделение, чтобы раскрыть населённые пункты.'
  );

  const sectionTitle = title || (
    mode === 'created'
      ? 'Созданные заказы — по подразделениям'
      : mode === 'general'
        ? 'Завершённые заказы — по подразделениям'
        : mode === 'sources'
          ? 'Источники — по подразделениям и населённым пунктам'
          : 'Разбивка по подразделениям'
  );

  const resolvedIcon = icon || (
    (mode === 'created' || mode === 'employee')
      ? Sparkles
      : (mode === 'general' || mode === 'sources')
        ? Target
        : mode === 'discounts'
          ? Percent
          : Building2
  );

  const resolvedTone = tone || (
    (mode === 'created' || mode === 'employee')
      ? 'blue'
      : (mode === 'general' || mode === 'sources')
        ? 'emerald'
        : 'blue'
  );

  const toneMeta = SURFACE_TONES[resolvedTone] || SURFACE_TONES.blue;
  const maxCount = Math.max(1, ...viewRows.map((row) => Number(row.count || 0)));
  const isClickable = typeof onRowClick === 'function';

  return (
    <SectionCard title={sectionTitle} icon={resolvedIcon} tone={resolvedTone} collapsible defaultOpen={defaultOpen}>
      {viewRows.length ? (
        <div className="space-y-3">
          {subtitle ? <div className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</div> : null}
          {viewRows.map((row) => {
            const isExpanded = expandedDeptKeys.has(row.deptKey);
            return (
              <div
                key={row.deptKey}
                className="overflow-hidden rounded-2xl bg-gray-50 ring-1 ring-gray-200/60 transition-all dark:bg-gray-950/40 dark:ring-gray-800"
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    haptic('tap');
                    toggleDept(row.deptKey);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleDept(row.deptKey);
                    }
                  }}
                  className="w-full cursor-pointer p-3.5 text-left transition-colors hover:bg-gray-100/70 dark:hover:bg-gray-900/60"
                >
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate font-medium text-gray-900 dark:text-gray-100">{row.name}</span>
                      {row.localities.length > 0 && (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-gray-200/70 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                          <MapPin className="h-2.5 w-2.5" />
                          {row.localities.length} нас. п.
                        </span>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className={`font-semibold ${toneMeta.text}`}>{formatMetric(row.count)}</span>
                      <ChevronDown
                        className={`h-4 w-4 text-gray-400 transition-transform duration-200 dark:text-gray-500 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </div>

                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.max(6, (Number(row.count || 0) / maxCount) * 100)}%` }}
                      transition={{ duration: 0.45, ease: 'easeOut' }}
                      className={`h-full rounded-full ${toneMeta.bar}`}
                    />
                  </div>

                  {mode === 'sources' && Array.isArray(row.sources) && row.sources.length > 0 && (
                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                      {row.sources.map((s) => (
                        <span
                          key={s.source}
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800 ring-1 ring-inset ring-emerald-600/20 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-500/30"
                        >
                          <span>{s.source}:</span>
                          <span className="font-semibold">{formatMetric(s.orders_count)}</span>
                          <span className="text-[10px] opacity-75">({Math.round(s.share_percent)}%)</span>
                        </span>
                      ))}
                    </div>
                  )}

                  {row.note ? <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">{row.note}</div> : null}
                </div>

                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: 'easeInOut' }}
                      className="overflow-hidden border-t border-gray-200/80 bg-white/60 p-3 dark:border-gray-800/80 dark:bg-gray-900/50"
                    >
                      <div className="space-y-2">
                        {mode === 'sources' && Array.isArray(row.sources) && row.sources.length > 0 && (
                          <div className="mb-3 space-y-1.5 rounded-xl border border-emerald-200/70 bg-emerald-50/50 p-2.5 dark:border-emerald-900/50 dark:bg-emerald-950/25">
                            <div className="flex items-center justify-between text-xs font-semibold text-emerald-900 dark:text-emerald-200">
                              <span>Источники в подразделении</span>
                              <span>{formatMetric(row.count)} зак.</span>
                            </div>
                            <div className="grid gap-1.5 sm:grid-cols-2">
                              {row.sources.map((s) => (
                                <div
                                  key={s.source}
                                  className="flex items-center justify-between rounded-lg bg-white/90 px-2.5 py-1.5 text-xs shadow-sm ring-1 ring-emerald-200/60 dark:bg-gray-900/90 dark:ring-emerald-800/40"
                                >
                                  <span className="truncate font-medium text-gray-800 dark:text-gray-200">{s.source}</span>
                                  <div className="flex shrink-0 items-center gap-2">
                                    <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                                      {formatMetric(s.orders_count)} зак.
                                    </span>
                                    <span className="rounded bg-gray-100 px-1 py-0.5 text-[10px] text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                                      {s.share_percent}%
                                    </span>
                                    {Number(s.revenue_total) > 0 && (
                                      <span className="text-[11px] font-medium text-gray-600 dark:text-gray-300">
                                        {formatMoney(s.revenue_total)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex items-center justify-between gap-2 pb-1 text-xs">
                          <span className="font-medium text-gray-500 dark:text-gray-400">
                            Населённые пункты {row.localities.length ? `(${row.localities.length})` : ''}
                          </span>
                          {isClickable && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                haptic('tap');
                                onRowClick(row.raw, null);
                              }}
                              className={`inline-flex items-center gap-1 font-semibold ${toneMeta.text} transition-opacity hover:opacity-80`}
                            >
                              <span>Все заказы ({formatMetric(row.count)})</span>
                              <ExternalLink className="h-3 w-3" />
                            </button>
                          )}
                        </div>

                        {row.localities.length ? (
                          <div className="space-y-1.5">
                            {row.localities.map((loc) => {
                              const isDiscountsMode = mode === 'discounts';
                              const locCount = isDiscountsMode
                                ? (loc.discounted_orders_count !== undefined
                                    ? Number(loc.discounted_orders_count || 0)
                                    : Number(loc.orders_count || 0))
                                : Number(loc.orders_count || 0);
                              const maxLocCount = Math.max(
                                1,
                                ...row.localities.map((l) =>
                                  Number(
                                    (isDiscountsMode && l.discounted_orders_count !== undefined
                                      ? l.discounted_orders_count
                                      : l.orders_count) || 0
                                  )
                                )
                              );
                              const deptBaseCount = isDiscountsMode && loc.discounted_orders_count === undefined
                                ? Number(row.raw?.orders_count || row.count || 0)
                                : Number(row.count || 0);
                              const locPctOfDept = deptBaseCount > 0
                                ? Math.min(100, Math.round((locCount / deptBaseCount) * 100))
                                : 0;
                              const isLocClickable = isClickable;
                              const isSelfPickup = String(loc.name || '').trim().toLowerCase() === 'самовывоз';

                              return (
                                <div
                                  key={loc.name}
                                  role={isLocClickable ? 'button' : undefined}
                                  tabIndex={isLocClickable ? 0 : undefined}
                                  onClick={isLocClickable ? (e) => {
                                    e.stopPropagation();
                                    haptic('tap');
                                    onRowClick(row.raw, loc.name);
                                  } : undefined}
                                  onKeyDown={isLocClickable ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      onRowClick(row.raw, loc.name);
                                    }
                                  } : undefined}
                                  className={`rounded-xl border border-gray-200/60 bg-white px-3 py-2 text-left transition-colors dark:border-gray-800 dark:bg-gray-950/70 ${
                                    isLocClickable ? 'cursor-pointer hover:border-gray-300 hover:bg-gray-50/80 dark:hover:border-gray-700 dark:hover:bg-gray-900/80' : ''
                                  }`}
                                >
                                  <div className="flex items-center justify-between gap-2 text-xs">
                                    <div className="flex min-w-0 items-center gap-1.5">
                                      {isSelfPickup ? (
                                        <UserCheck className="h-3.5 w-3.5 shrink-0 text-violet-600 dark:text-violet-400" />
                                      ) : (
                                        <MapPin className={`h-3.5 w-3.5 shrink-0 ${toneMeta.text}`} />
                                      )}
                                      <span className="truncate font-medium text-gray-800 dark:text-gray-200">{loc.name}</span>
                                    </div>
                                    <div className="flex shrink-0 items-center gap-2">
                                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                                        {formatMetric(locCount)} зак.
                                      </span>
                                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                                        {locPctOfDept}%
                                      </span>
                                    </div>
                                  </div>

                                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                                    <div
                                      style={{ width: `${locCount > 0 ? Math.max(4, (locCount / maxLocCount) * 100) : 0}%` }}
                                      className={`h-full rounded-full ${toneMeta.bar} opacity-75`}
                                    />
                                  </div>

                                  <div className="mt-1 flex flex-wrap items-center justify-between gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                                    {isDiscountsMode ? (
                                      <>
                                        <span>{formatMetric(loc.orders_count)} зак. в срезе</span>
                                        {Number(loc.discounts_total) > 0 ? (
                                          <span>
                                            скидки {formatMoney(loc.discounts_total)}
                                            {Number(loc.average_discount) > 0 ? ` • средняя ${formatMoney(loc.average_discount)}` : ''}
                                          </span>
                                        ) : (
                                          <span>скидок нет</span>
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        <span>{formatMetric(loc.positions_count)} поз.</span>
                                        {Number(loc.revenue_total) > 0 ? (
                                          <span>{formatMoney(loc.revenue_total)}</span>
                                        ) : null}
                                      </>
                                    )}
                                  </div>

                                  {mode === 'sources' && Array.isArray(loc.sources) && loc.sources.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1 border-t border-gray-100 pt-1.5 dark:border-gray-800/80">
                                      {loc.sources.map((s) => (
                                        <span
                                          key={s.source}
                                          className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                                        >
                                          <span>{s.source}:</span>
                                          <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                                            {formatMetric(s.orders_count)}
                                          </span>
                                          <span className="text-[9px] text-gray-400 dark:text-gray-500">
                                            ({Math.round(s.share_percent)}%)
                                          </span>
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="rounded-xl bg-gray-100/60 p-2.5 text-center text-xs text-gray-500 dark:bg-gray-800/40 dark:text-gray-400">
                            Нет данных по населённым пунктам
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      ) : (
        <DataEmptyState title="Нет данных по подразделениям" description="За выбранный период подходящих заказов не найдено." />
      )}
    </SectionCard>
  );
};

const StageBreakdown = ({ rows, onStageSelect = null }) => (
  <div className="space-y-3">
    {rows.map((row) => {
      const stage = getStageConfig(row.stage_name);
      const Icon = stage.icon;
      const badgeClass = STAGE_BADGE_STYLES[stage.color] || 'bg-gray-100 text-gray-700';
      const gradientClass = STAGE_BG_STYLES[stage.color] || 'from-gray-500 to-gray-600';
      const isClickable = typeof onStageSelect === 'function';

      return (
        <button
          key={row.stage_name}
          type="button"
          onClick={() => {
            if (!isClickable) return;
            haptic('tap');
            onStageSelect(row.stage_name);
          }}
          className={`w-full rounded-2xl bg-gray-50 px-3 py-3 text-left dark:bg-gray-950/40 ${isClickable ? 'transition-colors hover:bg-gray-100 dark:hover:bg-gray-900' : ''}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className={`flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br text-white ${gradientClass}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{row.stage_label}</div>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ${badgeClass}`}>
                  {formatMetric(row.orders_count)} зак.
                </span>
                <span className="inline-flex items-center rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-gray-600 ring-1 ring-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:ring-gray-700">
                  {formatMetric(row.items_count)} предм.
                </span>
                {(Number(row.rework_items_count) || 0) > 0 ? (
                  <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:ring-amber-800">
                    На доработку: {formatMetric(row.rework_items_count)} предм.
                  </span>
                ) : null}
                {(Number(row.total_area) || 0) > 0 ? (
                  <span className="inline-flex items-center rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-gray-600 ring-1 ring-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:ring-gray-700">
                    {formatMetric(row.total_area)} м²
                  </span>
                ) : null}
                {(Number(row.total_weight) || 0) > 0 ? (
                  <span className="inline-flex items-center rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-gray-600 ring-1 ring-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:ring-gray-700">
                    {formatMetric(row.total_weight)} кг
                  </span>
                ) : null}
                {(Number(row.floors_count) || 0) > 0 ? (
                  <span className="inline-flex items-center rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-gray-600 ring-1 ring-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:ring-gray-700">
                    Этажи: {formatMetric(row.floors_count)}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="shrink-0 text-xs font-medium text-gray-400 dark:text-gray-500">
              {formatStageValue(row.stage_name, row)}
            </div>
          </div>
        </button>
      );
    })}
  </div>
);

const DetailStatCard = ({
  label,
  value,
  note = null,
  onClick = null,
}) => {
  const isClickable = typeof onClick === 'function';

  return (
    <button
      type="button"
      onClick={() => {
        if (!isClickable) return;
        haptic('tap');
        onClick();
      }}
      className={`rounded-2xl bg-gray-50 px-3 py-3 text-left dark:bg-gray-950/40 ${isClickable ? 'transition-colors hover:bg-gray-100 dark:hover:bg-gray-900' : ''}`}
    >
      <div className="text-[11px] uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">{label}</div>
      <div className="mt-2 text-xl font-semibold text-gray-950 dark:text-white">{value}</div>
      {note ? <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{note}</div> : null}
    </button>
  );
};

const Statistics = () => {
  const { toast, showToast, hideToast } = useToast(3200);
  const [resolvedUserId, setResolvedUserId] = useState(0);
  const userType = localStorage.getItem('user_type');
  const isAdminLike = userType === 'admin' || userType === 'developer';
  const [currentAuthUser, setCurrentAuthUser] = useState(null);

  const [activeTab, setActiveTab] = useState('employee');
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [showCompactFilters, setShowCompactFilters] = useState(false);
  const [stageDetailsModal, setStageDetailsModal] = useState(null);
  const [stageDetailsLoading, setStageDetailsLoading] = useState(false);
  const [stageDetailsError, setStageDetailsError] = useState('');
  const [expandedStageOrderId, setExpandedStageOrderId] = useState(null);
  const [orderDetailsModal, setOrderDetailsModal] = useState(null);
  const [orderDetailsLoading, setOrderDetailsLoading] = useState(false);
  const [orderDetailsError, setOrderDetailsError] = useState('');
  const [expandedDetailsOrderId, setExpandedDetailsOrderId] = useState(null);
  const [selectedOrderDetailsDept, setSelectedOrderDetailsDept] = useState('all');

  const modalOrders = useMemo(
    () => (Array.isArray(orderDetailsModal?.orders) ? orderDetailsModal.orders : []),
    [orderDetailsModal?.orders]
  );
  const orderDetailsDepartmentOptions = useMemo(() => {
    if (!modalOrders.length) return [];
    const counts = new Map();
    for (const order of modalOrders) {
      const key = order.department_id !== null && order.department_id !== undefined ? String(order.department_id) : 'none';
      const name = order.department_name || (key === 'none' ? 'Без подразделения' : `Подразделение #${key}`);
      if (!counts.has(key)) {
        counts.set(key, { key, name, count: 0 });
      }
      counts.get(key).count += 1;
    }
    return Array.from(counts.values());
  }, [modalOrders]);

  const displayedModalOrders = useMemo(() => {
    if (!selectedOrderDetailsDept || selectedOrderDetailsDept === 'all') {
      return modalOrders;
    }
    return modalOrders.filter((order) => {
      const key = order.department_id !== null && order.department_id !== undefined ? String(order.department_id) : 'none';
      return key === selectedOrderDetailsDept;
    });
  }, [modalOrders, selectedOrderDetailsDept]);

  const initialRange = useMemo(() => getCurrentMonthRange(), []);
  const [draftFilters, setDraftFilters] = useState(() => ({
    ...initialRange,
    stage: '',
    user_id: '',
  }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    ...initialRange,
    stage: '',
    user_id: '',
  }));
  const activeRequestIdRef = useRef(0);

  const tabs = isAdminLike ? ADMIN_TABS : EMPLOYEE_TABS;
  const appliedStageConfig = appliedFilters.stage ? getStageConfig(appliedFilters.stage) : null;
  const stageOptions = useMemo(
    () => [
      { value: '', label: 'Все этапы', icon: Layers3 },
      ...USER_STAGE_ORDER.map((stageName) => {
        const config = getStageConfig(stageName);
        return {
          value: stageName,
          label: config.label,
          icon: config.icon,
        };
      }),
    ],
    []
  );

  const userOptions = useMemo(
    () => users.map((user) => ({
        value: String(user.id),
        label: formatStatisticsUserLabel(user),
        icon: UserRound,
      })),
    [users]
  );

  const selfUserOption = useMemo(() => {
    const selfUserId = Number(currentAuthUser?.id || resolvedUserId || localStorage.getItem('user_id') || 0) || 0;
    if (!selfUserId) return null;

    const user = {
      id: selfUserId,
      login: currentAuthUser?.login || '',
      first_name: currentAuthUser?.first_name || '',
      last_name: currentAuthUser?.last_name || '',
      middle_name: currentAuthUser?.middle_name || '',
      name: currentAuthUser?.name || '',
    };

    return {
      value: String(selfUserId),
      label: formatStatisticsUserLabel(user),
      icon: UserRound,
    };
  }, [currentAuthUser, resolvedUserId]);

  const fixedUserOptions = useMemo(
    () => (selfUserOption ? [selfUserOption] : []),
    [selfUserOption]
  );

  const hasDraftChanges = useMemo(
    () => !areFiltersEqual(draftFilters, appliedFilters),
    [draftFilters, appliedFilters]
  );

  const periodPreset = useMemo(() => {
    const monthRange = getCurrentMonthRange();
    if (draftFilters.date_from === monthRange.date_from && draftFilters.date_to === monthRange.date_to) return 'month';
    const last7 = getLastDaysRange(7);
    if (draftFilters.date_from === last7.date_from && draftFilters.date_to === last7.date_to) return '7';
    const last30 = getLastDaysRange(30);
    if (draftFilters.date_from === last30.date_from && draftFilters.date_to === last30.date_to) return '30';
    return '';
  }, [draftFilters.date_from, draftFilters.date_to]);

  const ensureCurrentUserId = useCallback(async () => {
    const authData = await getCurrentAuthUser();
    const parsedId = Number(authData?.user_id || 0) || 0;
    if (parsedId) {
      localStorage.setItem('user_id', String(parsedId));
      if (parsedId !== resolvedUserId) {
        setResolvedUserId(parsedId);
      }
      setCurrentAuthUser((prev) => {
        const next = {
          id: parsedId,
          login: authData?.login || '',
          first_name: authData?.first_name || '',
          last_name: authData?.last_name || '',
          middle_name: authData?.middle_name || '',
          user_type: authData?.user_type || userType || '',
        };
        if (
          prev?.id === next.id
          && prev?.login === next.login
          && prev?.first_name === next.first_name
          && prev?.last_name === next.last_name
          && prev?.middle_name === next.middle_name
          && prev?.user_type === next.user_type
        ) {
          return prev;
        }
        return next;
      });

      if (!isAdminLike) {
        const selfUserId = String(parsedId);
        setDraftFilters((prev) => (prev.user_id === selfUserId ? prev : { ...prev, user_id: selfUserId }));
        setAppliedFilters((prev) => (prev.user_id === selfUserId ? prev : { ...prev, user_id: selfUserId }));
      }
    }
    return parsedId || resolvedUserId;
  }, [isAdminLike, resolvedUserId, userType]);

  const fetchUsers = useCallback(async () => {
    if (!isAdminLike) return;
    setLoadingUsers(true);
    try {
      const loadedUsers = await getStatisticsUsers();
      setUsers(loadedUsers);
      setDraftFilters((prev) => {
        if (prev.user_id && loadedUsers.some((user) => String(user.id) === String(prev.user_id))) return prev;
        const preferred = loadedUsers.find((user) => Number(user.id) === Number(resolvedUserId)) || loadedUsers[0];
        return { ...prev, user_id: preferred ? String(preferred.id) : '' };
      });
      setAppliedFilters((prev) => {
        if (prev.user_id && loadedUsers.some((user) => String(user.id) === String(prev.user_id))) return prev;
        const preferred = loadedUsers.find((user) => Number(user.id) === Number(resolvedUserId)) || loadedUsers[0];
        return { ...prev, user_id: preferred ? String(preferred.id) : '' };
      });
    } catch (requestError) {
      console.warn('Не удалось загрузить пользователей для статистики:', requestError);
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        setError('Нет подключения к интернету. Проверьте сеть.');
      }
    } finally {
      setLoadingUsers(false);
    }
  }, [isAdminLike, resolvedUserId]);

  const loadStatistics = useCallback(async () => {
    const requestId = ++activeRequestIdRef.current;
    setLoading(true);
    setError('');

    try {
      const currentUserId = await ensureCurrentUserId();
      const params = {
        date_from: appliedFilters.date_from,
        date_to: appliedFilters.date_to,
        stage: appliedFilters.stage || undefined,
      };

      let response = null;
      if (activeTab === 'employee') {
        const targetUserId = isAdminLike
          ? appliedFilters.user_id || currentUserId
          : currentUserId || appliedFilters.user_id;
        response = await getEmployeeStatistics({
          ...params,
          user_id: targetUserId || undefined,
        });
      } else if (activeTab === 'general') {
        response = await getGeneralStatistics(params);
      } else if (activeTab === 'discounts') {
        response = await getDiscountStatistics(params);
      } else if (activeTab === 'sources') {
        response = await getSourceStatistics(params);
      }

      if (requestId !== activeRequestIdRef.current) return;
      setData(response);
    } catch (requestError) {
      if (requestId !== activeRequestIdRef.current) return;

      const isActualOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
      const rawMsg = String(requestError?.message || '').toLowerCase();
      const isTimeout = rawMsg.includes('сервер не отвечает')
        || rawMsg.includes('timeout')
        || rawMsg.includes('aborterror')
        || rawMsg.includes('превышено время')
        || rawMsg.includes('504');

      let userMessage = requestError?.message || 'Не удалось загрузить статистику';
      if (isActualOffline) {
        userMessage = 'Нет подключения к интернету. Проверьте сеть.';
      } else if (isTimeout) {
        userMessage = 'Время ожидания ответа сервера истекло. Запрос за выбранный период обрабатывался слишком долго. Попробуйте повторить попытку или выбрать меньший диапазон дат.';
      }

      setError(userMessage);
      setData(null);
    } finally {
      if (requestId === activeRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [activeTab, appliedFilters, ensureCurrentUserId, isAdminLike]);

  useEffect(() => {
    ensureCurrentUserId().catch((requestError) => {
      console.warn('Не удалось определить пользователя для статистики:', requestError);
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        setError('Нет подключения к интернету. Проверьте сеть.');
      }
    });
  }, [ensureCurrentUserId]);

  useEffect(() => {
    if (isAdminLike) fetchUsers();
  }, [fetchUsers, isAdminLike]);

  useEffect(() => {
    if (activeTab === 'employee' && isAdminLike && !appliedFilters.user_id && users.length > 0) return;
    loadStatistics();
  }, [activeTab, appliedFilters, isAdminLike, loadStatistics, users.length]);

  useEffect(() => {
    const isActualOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
    if (!error || !isActualOffline) return undefined;

    const retryWhenAvailable = () => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      loadStatistics();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        retryWhenAvailable();
      }
    };

    window.addEventListener('online', retryWhenAvailable);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('online', retryWhenAvailable);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [error, loadStatistics]);

  const applyFilters = () => {
    haptic('medium');
    const selfUserId = String(currentAuthUser?.id || resolvedUserId || draftFilters.user_id || '');
    setAppliedFilters(isAdminLike ? { ...draftFilters } : { ...draftFilters, user_id: selfUserId });
    setShowCompactFilters(false);
  };

  const resetFilters = () => {
    haptic('tap');
    const monthRange = getCurrentMonthRange();
    const userId = isAdminLike
      ? (
        draftFilters.user_id
        || appliedFilters.user_id
        || (users.find((user) => Number(user.id) === Number(resolvedUserId)) ? String(resolvedUserId) : (users[0] ? String(users[0].id) : ''))
      )
      : String(currentAuthUser?.id || resolvedUserId || appliedFilters.user_id || draftFilters.user_id || '');
    const next = { ...monthRange, stage: '', user_id: userId };
    setDraftFilters(next);
    setAppliedFilters(next);
    setShowCompactFilters(false);
  };

  const setPresetRange = (preset) => {
    haptic('selection');
    const nextRange = preset === 'month' ? getCurrentMonthRange() : getLastDaysRange(Number(preset));
    setDraftFilters((prev) => ({ ...prev, ...nextRange }));
  };

  const aggregatedStageItems = useMemo(() => {
    const mapping = new Map();
    for (const stageRow of data?.stages || []) {
      for (const item of stageRow.items_by_type || []) {
        const current = mapping.get(item.name) || 0;
        mapping.set(item.name, current + Number(item.count || 0));
      }
    }
    return Array.from(mapping.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((left, right) => right.count - left.count)
      .slice(0, 8);
  }, [data?.stages]);

  const closeStageDetailsModal = useCallback(() => {
    setStageDetailsModal(null);
    setStageDetailsLoading(false);
    setStageDetailsError('');
    setExpandedStageOrderId(null);
  }, []);

  const openStageDetailsModal = useCallback(async (stageName, executionScope = 'original') => {
    if (!stageName) return;
    const stageConfig = getStageConfig(stageName);
    const isGeneralDetails = activeTab === 'general';
    const isRepeatExecution = executionScope === 'repeat';
    setStageDetailsModal({
      stage_name: stageName,
      stage_label: isRepeatExecution ? `Повторные выполнения: ${stageConfig.label}` : stageConfig.label,
      description: isRepeatExecution
        ? (isGeneralDetails
          ? 'Все повторные выполнения на этом этапе за выбранный период.'
          : 'Повторные выполнения сотрудника на этом этапе за выбранный период.')
        : (isGeneralDetails
          ? 'Первые выполнения заказов и предметов на этом этапе за выбранный период.'
          : 'Заказы и предметы, которые сотрудник выполнил на этом этапе впервые.'),
      execution_scope: executionScope,
      orders: [],
    });
    setStageDetailsLoading(true);
    setStageDetailsError('');
    setExpandedStageOrderId(null);
    try {
      const commonParams = {
        date_from: appliedFilters.date_from,
        date_to: appliedFilters.date_to,
        stage: stageName,
        execution_scope: executionScope,
      };
      let payload;
      if (isGeneralDetails) {
        payload = await getGeneralStageDetails(commonParams);
      } else {
        const currentUserId = await ensureCurrentUserId();
        payload = await getEmployeeStageDetails({
          ...commonParams,
          user_id: isAdminLike ? appliedFilters.user_id || currentUserId : currentUserId || appliedFilters.user_id || undefined,
        });
      }
      setStageDetailsModal({
        stage_name: payload?.stage?.name || stageName,
        stage_label: isRepeatExecution
          ? `Повторные выполнения: ${payload?.stage?.label || stageConfig.label}`
          : (payload?.stage?.label || stageConfig.label),
        description: payload?.description || (isGeneralDetails
          ? 'Все заказы и предметы, выполненные на этом этапе за выбранный период.'
          : 'Заказы и предметы, которые сотрудник выполнил на этом этапе.'),
        execution_scope: executionScope,
        orders: Array.isArray(payload?.orders) ? payload.orders : [],
      });
      setExpandedStageOrderId(null);
    } catch (requestError) {
      setStageDetailsError(requestError.message || 'Не удалось загрузить детали этапа');
    } finally {
      setStageDetailsLoading(false);
    }
  }, [activeTab, appliedFilters.date_from, appliedFilters.date_to, appliedFilters.user_id, ensureCurrentUserId, isAdminLike]);

  const openGeneralAdditionalServiceDetails = useCallback(async (row, executionScope = 'original') => {
    const serviceName = row?.name;
    if (!serviceName) return;
    const isRepeatExecution = executionScope === 'repeat';
    setStageDetailsModal({
      stage_name: 'additional_service',
      stage_label: `${isRepeatExecution ? 'Повторная допуслуга' : 'Выполненная допуслуга'}: ${serviceName}`,
      description: isRepeatExecution
        ? 'Повторные выполнения, в которых эта допуслуга учтена за выбранный период.'
        : 'Заказы, в которых эта допуслуга учтена при первом выполнении за выбранный период.',
      orders: [],
    });
    setStageDetailsLoading(true);
    setStageDetailsError('');
    setExpandedStageOrderId(null);
    try {
      const payload = await getGeneralAdditionalServiceDetails({
        service_name: serviceName,
        date_from: appliedFilters.date_from,
        date_to: appliedFilters.date_to,
        stage: appliedFilters.stage || undefined,
        execution_scope: executionScope,
      });
      setStageDetailsModal({
        stage_name: 'additional_service',
        stage_label: `${isRepeatExecution ? 'Повторная допуслуга' : 'Выполненная допуслуга'}: ${payload?.title || serviceName}`,
        description: payload?.description || 'Заказы с выбранной выполненной допуслугой.',
        execution_scope: executionScope,
        orders: Array.isArray(payload?.orders) ? payload.orders : [],
      });
      setExpandedStageOrderId(null);
    } catch (requestError) {
      setStageDetailsError(requestError.message || 'Не удалось загрузить заказы по допуслуге');
    } finally {
      setStageDetailsLoading(false);
    }
  }, [appliedFilters.date_from, appliedFilters.date_to, appliedFilters.stage]);

  const closeOrderDetailsModal = useCallback(() => {
    setOrderDetailsModal(null);
    setOrderDetailsLoading(false);
    setOrderDetailsError('');
    setExpandedDetailsOrderId(null);
    setSelectedOrderDetailsDept('all');
  }, []);

  useModalBackButton(!!stageDetailsModal, closeStageDetailsModal);
  useModalBackButton(!!orderDetailsModal, closeOrderDetailsModal);

  const openOrderDetailsModal = useCallback(async (mode, submode = null, title = null) => {
    if (!mode) return;
    const fallbackTitle = title || (mode === 'finance' ? 'Финансы по закрытым заказам' : 'Созданные заказы');
    setSelectedOrderDetailsDept('all');
    setOrderDetailsModal({ mode, submode, title: fallbackTitle, description: '', orders: [] });
    setOrderDetailsLoading(true);
    setOrderDetailsError('');
    setExpandedDetailsOrderId(null);
    try {
      const currentUserId = await ensureCurrentUserId();
      const payload = await getEmployeeOrderDetails({
        user_id: isAdminLike ? appliedFilters.user_id || currentUserId : currentUserId || appliedFilters.user_id || undefined,
        date_from: appliedFilters.date_from,
        date_to: appliedFilters.date_to,
        mode,
        submode: submode || undefined,
      });
      setOrderDetailsModal({
        mode: payload?.mode || mode,
        submode: payload?.submode || submode,
        title: payload?.title || fallbackTitle,
        description: payload?.description || '',
        date_kind: payload?.date_kind || (mode === 'finance' ? 'completed' : 'created'),
        show_finance: payload?.show_finance ?? (mode === 'finance'),
        orders: Array.isArray(payload?.orders) ? payload.orders : [],
      });
      setExpandedDetailsOrderId(null);
    } catch (requestError) {
      setOrderDetailsError(requestError.message || 'Не удалось загрузить детали заказов');
    } finally {
      setOrderDetailsLoading(false);
    }
  }, [appliedFilters.date_from, appliedFilters.date_to, appliedFilters.user_id, ensureCurrentUserId, isAdminLike]);

  const openGeneralOrderDetails = useCallback(async (mode, value = null, title = null, locality = null) => {
    if (!mode) return;
    const fallbackTitle = title || 'Заказы общей статистики';
    setSelectedOrderDetailsDept('all');
    setOrderDetailsModal({
      mode: 'general',
      detail_mode: mode,
      value,
      locality,
      title: fallbackTitle,
      description: '',
      date_kind: 'created',
      show_finance: false,
      orders: [],
    });
    setOrderDetailsLoading(true);
    setOrderDetailsError('');
    setExpandedDetailsOrderId(null);
    try {
      const payload = await getGeneralOrderDetails({
        mode,
        value: value || undefined,
        locality: locality || undefined,
        date_from: appliedFilters.date_from,
        date_to: appliedFilters.date_to,
        stage: appliedFilters.stage || undefined,
      });
      setOrderDetailsModal({
        mode: 'general',
        detail_mode: payload?.mode || mode,
        value: payload?.value || value,
        locality: payload?.locality || locality,
        title: payload?.title || fallbackTitle,
        description: payload?.description || '',
        date_kind: payload?.date_kind || 'created',
        show_finance: Boolean(payload?.show_finance),
        orders: Array.isArray(payload?.orders) ? payload.orders : [],
      });
      setExpandedDetailsOrderId(null);
    } catch (requestError) {
      setOrderDetailsError(requestError.message || 'Не удалось загрузить заказы общей статистики');
    } finally {
      setOrderDetailsLoading(false);
    }
  }, [appliedFilters.date_from, appliedFilters.date_to, appliedFilters.stage]);

  const renderEmployeeView = () => {
    if (!data) return null;
    const creation = data.creation || {};
    const payment = data.payment || {};
    const tasks = data.tasks || {};
    const deferredHandling = data.deferred_handling || {};
    const deliveryStage = (data.stages || []).find((row) => row.stage_name === 'delivery');
    const totalFloors = Number(deliveryStage?.floors_count) || 0;
    const reworkItemsCount = Number(data.summary?.rework_items) || 0;
    const reworkStageRows = (data.stages || []).filter((row) => (Number(row.rework_items_count) || 0) > 0);
    const showCreation = [creation.orders_count, creation.positions_count, creation.orders_without_items].some((value) => (Number(value) || 0) > 0);
    const showOrderStatuses = [creation.orders_in_progress, creation.orders_completed, creation.orders_cancelled].some((value) => (Number(value) || 0) > 0);
    const createdAdditionalServices = data.created_additional_services || data.additional_services || [];
    const completedAdditionalServices = data.completed_additional_services || [];
    const repeatStages = data.repeat_stages || [];
    const repeatCompletedAdditionalServices = data.repeat_completed_additional_services || [];
    const showCreatedAdditionalServices = Boolean(createdAdditionalServices.length);
    const showCompletedAdditionalServices = Boolean(completedAdditionalServices.length);
    const showStages = Boolean(data.stages?.length);
    const showRepeatExecutions = Boolean(repeatStages.length || repeatCompletedAdditionalServices.length);
    const showPayment = [payment.total_received, payment.cash_received, payment.card_received, payment.settlement_received].some((value) => (Number(value) || 0) > 0);
    const showTasks = [tasks.created_count, tasks.completed_count, tasks.cancelled_count, tasks.active_assigned_count].some((value) => (Number(value) || 0) > 0);
    const showFloors = totalFloors > 0;
    const showRework = reworkItemsCount > 0;
    const showDeferredHandling = (Number(deferredHandling.handled_count) || 0) > 0;
    const hasVisibleBlocks = showCreation || showOrderStatuses || showCreatedAdditionalServices || showCompletedAdditionalServices || showStages || showRepeatExecutions || showPayment || showTasks || showFloors || showRework || showDeferredHandling;

    if (!hasVisibleBlocks) {
      return (
        <DataEmptyState
          title="Нет данных за выбранный период"
          description="По этому сотруднику пока не накопилось действий, которые можно показать в статистике."
        />
      );
    }

    return (
      <div className="space-y-7">
        <section className="space-y-3">
          <div className="flex items-center gap-3 px-1">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600 dark:text-blue-300">Созданное</div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Что сотрудник добавил в систему</div>
            </div>
            <div className="h-px flex-1 bg-blue-200 dark:bg-blue-900/70" />
          </div>

          <SectionCard title="Созданные заказы" icon={Sparkles} tone="blue" collapsible defaultOpen={false}>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
              <DetailStatCard
                label="Всего"
                value={formatMetric(creation.orders_count)}
                note="Открыть список заказов"
                onClick={() => openOrderDetailsModal('creation', 'orders', 'Созданные заказы')}
              />
              <DetailStatCard label="В работе" value={formatMetric(creation.orders_in_progress)} note="Среди созданных" />
              <DetailStatCard label="Завершено" value={formatMetric(creation.orders_completed)} note="Среди созданных" />
              <DetailStatCard label="Отменено" value={formatMetric(creation.orders_cancelled)} note="Среди созданных" />
              <DetailStatCard
                label="Без предметов"
                value={formatMetric(creation.orders_without_items)}
                note="Открыть пустые заказы"
                onClick={() => openOrderDetailsModal('creation', 'without_items', 'Заказы без предметов')}
              />
            </div>
          </SectionCard>

          <SectionCard title="Добавленные предметы" icon={Box} tone="emerald" collapsible defaultOpen={false}>
            <DetailStatCard
              label="Всего предметов"
              value={formatMetric(creation.positions_count)}
              note="Открыть заказы с добавленными предметами"
              onClick={() => openOrderDetailsModal('creation', 'positions', 'Заказы с добавленными предметами')}
            />
          </SectionCard>

          <SectionCard title="Добавленные услуги" icon={Plus} tone="rose" collapsible defaultOpen={false}>
            {showCreatedAdditionalServices ? (
              <HorizontalBars
                rows={createdAdditionalServices.map((item) => ({ name: item.name, count: item.count }))}
                tone="rose"
                subtitle="Услуги в заказах, которые создал сотрудник. Это не выполненная работа."
              />
            ) : (
              <DataEmptyState title="Нет добавленных услуг" description="В созданных за период заказах допуслуги не добавлялись." />
            )}
          </SectionCard>

          <DepartmentBreakdown rows={data.departments || []} mode="employee" />

          {(Number(tasks.created_count) || 0) > 0 ? (
            <SectionCard title="Созданные поручения" icon={ListTodo} tone="blue" collapsible defaultOpen={false}>
              <DetailStatCard label="Создано" value={formatMetric(tasks.created_count)} note="Поручения, созданные сотрудником" />
            </SectionCard>
          ) : null}
        </section>

        <section className="space-y-3">
          <div className="flex items-center gap-3 px-1">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-violet-600 dark:text-violet-300">Выполненное</div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Что сотрудник фактически сделал</div>
            </div>
            <div className="h-px flex-1 bg-violet-200 dark:bg-violet-900/70" />
          </div>

          <SectionCard title={selectedStageConfig ? `Первое выполнение: ${selectedStageConfig.label}` : 'Этапы — первое выполнение'} icon={Layers3} tone="violet" collapsible defaultOpen={false}>
            {showStages ? (
              <StageBreakdown rows={data.stages} onStageSelect={openStageDetailsModal} />
            ) : (
              <DataEmptyState title="Нет первых выполнений" description="За выбранный период сотрудник не завершал этапы заказов впервые." />
            )}
          </SectionCard>

          {showRepeatExecutions ? (
            <SectionCard title="Повторные выполнения" icon={RefreshCw} tone="violet" collapsible defaultOpen={false}>
              <div className="space-y-5">
                {repeatStages.length ? (
                  <StageBreakdown rows={repeatStages} onStageSelect={(stageName) => openStageDetailsModal(stageName, 'repeat')} />
                ) : null}
                {repeatCompletedAdditionalServices.length ? (
                  <div className={`${repeatStages.length ? 'border-t border-gray-200 pt-4 dark:border-gray-800' : ''}`}>
                    <div className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-violet-600 dark:text-violet-300">Допуслуги в повторах</div>
                    <HorizontalBars
                      rows={repeatCompletedAdditionalServices.map((item) => ({ name: item.name, count: item.count }))}
                      tone="violet"
                      subtitle="Учитываются только в том повторном цикле, в котором были выполнены."
                    />
                  </div>
                ) : null}
              </div>
            </SectionCard>
          ) : null}

          <SectionCard title="Выполненные допуслуги" icon={Plus} tone="violet" collapsible defaultOpen={false}>
            {showCompletedAdditionalServices ? (
              <HorizontalBars
                 rows={completedAdditionalServices.map((item) => ({ name: item.name, count: item.count }))}
                 tone="violet"
                 subtitle="Только первое выполнение. Повторные допуслуги вынесены в отдельный аккордеон."
              />
            ) : (
              <DataEmptyState title="Нет выполненных допуслуг" description="На выполненных сотрудником предметах допуслуги за период не найдены." />
            )}
          </SectionCard>

          {showDeferredHandling ? (
            <SectionCard title="Отложенные задания" icon={Clock3} tone="amber" collapsible defaultOpen={false}>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <DetailStatCard label="Обработано" value={formatMetric(deferredHandling.handled_count)} note="Финальные решения диспетчера" />
                <DetailStatCard label="Заказы" value={formatMetric(deferredHandling.orders_handled_count)} note="Обработанные отложенные заказы" />
                <DetailStatCard label="Поручения" value={formatMetric(deferredHandling.tasks_handled_count)} note="Обработанные отложенные поручения" />
                <DetailStatCard label="В доступные" value={formatMetric(deferredHandling.returned_available_count)} note="Без назначения исполнителя" />
                <DetailStatCard label="Назначено" value={formatMetric(deferredHandling.assigned_count)} note="Сразу передано исполнителю" />
                <DetailStatCard label="Отменено" value={formatMetric(deferredHandling.cancelled_count)} note="Задание отменено диспетчером" />
                <DetailStatCard label="На доработку" value={formatMetric(deferredHandling.rework_count)} note="Доставка возвращена на повторную обработку" />
                <DetailStatCard label="Среднее время" value={`${formatMetric(deferredHandling.average_resolution_minutes)} мин`} note="От откладывания до решения" />
              </div>
            </SectionCard>
          ) : null}

          {showRework ? (
            <SectionCard title="Качество и доработки" icon={AlertTriangle} tone="amber" collapsible defaultOpen={false}>
              <div className="grid gap-3 sm:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
                <DetailStatCard
                  label="Отправлено на доработку"
                  value={`${formatMetric(reworkItemsCount)} предм.`}
                  note="Предметы, выполненные сотрудником и возвращённые на повторную обработку"
                />
                <div className="space-y-2 rounded-2xl bg-gray-50 px-3 py-3 dark:bg-gray-950/40">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">По этапам</div>
                  {reworkStageRows.map((row) => (
                    <div key={row.stage_name} className="flex items-center justify-between gap-3 text-sm">
                      <span className="min-w-0 truncate text-gray-600 dark:text-gray-300">{row.stage_label}</span>
                      <span className="shrink-0 font-semibold text-amber-700 dark:text-amber-300">{formatMetric(row.rework_items_count)} предм.</span>
                    </div>
                  ))}
                </div>
              </div>
            </SectionCard>
          ) : null}

          {[(tasks.completed_count), (tasks.cancelled_count), (tasks.active_assigned_count)].some((value) => (Number(value) || 0) > 0) ? (
            <SectionCard title="Работа с поручениями" icon={ListTodo} tone="blue" collapsible defaultOpen={false}>
              <div className="grid gap-3 sm:grid-cols-3">
                <DetailStatCard label="Выполнено" value={formatMetric(tasks.completed_count)} note="Засчитано сотруднику" />
                <DetailStatCard label="Отменено" value={formatMetric(tasks.cancelled_count)} note="Отменено этим сотрудником" />
                <DetailStatCard label="Активно сейчас" value={formatMetric(tasks.active_assigned_count)} note="Назначено сотруднику" />
              </div>
            </SectionCard>
          ) : null}

          {showFloors ? (
            <SectionCard title="Подъёмы на этаж" icon={Layers3} tone="amber" collapsible defaultOpen={false}>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between gap-3 rounded-2xl bg-gray-50 px-3 py-3 dark:bg-gray-950/40">
                  <span className="text-gray-500 dark:text-gray-400">Всего этажей по доставкам</span>
                  <span className="font-semibold text-gray-950 dark:text-white">{formatMetric(totalFloors)}</span>
                </div>
                <button
                  type="button"
                  onClick={() => openStageDetailsModal('delivery')}
                  className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  Посмотреть разбивку по заказам
                </button>
              </div>
            </SectionCard>
          ) : null}

          {showPayment ? (
            <SectionCard title="Финансы по выполненным заказам" icon={Coins} tone="amber" collapsible defaultOpen={false}>
              <div className="space-y-3 text-sm">
                <button
                  type="button"
                  onClick={() => {
                    haptic('tap');
                    openOrderDetailsModal('finance', 'total', 'Финансы по закрытым заказам');
                  }}
                  className="flex w-full items-center justify-between gap-3 rounded-2xl bg-gray-50 px-3 py-3 text-left transition-colors hover:bg-gray-100 dark:bg-gray-950/40 dark:hover:bg-gray-900"
                >
                  <span className="text-gray-500 dark:text-gray-400">Всего по закрытым заказам</span>
                  <span className="font-semibold text-gray-950 dark:text-white">{formatMoney(payment.total_received)}</span>
                </button>
                <div className="grid gap-3 sm:grid-cols-3">
                  <DetailStatCard label="Наличные" value={formatMoney(payment.cash_received)} note="Только наличная оплата" onClick={() => openOrderDetailsModal('finance', 'cash', 'Наличные по закрытым заказам')} />
                  <DetailStatCard label="Безнал" value={formatMoney(payment.card_received)} note="Карта, онлайн и перевод" onClick={() => openOrderDetailsModal('finance', 'card', 'Безнал по закрытым заказам')} />
                  <DetailStatCard label="Расчётный счёт" value={formatMoney(payment.settlement_received)} note="Оплата на расчётный счёт" onClick={() => openOrderDetailsModal('finance', 'settlement', 'Расчётный счёт по закрытым заказам')} />
                </div>
              </div>
            </SectionCard>
          ) : null}
        </section>
      </div>
    );
  };

  const renderGeneralView = () => {
    if (!data) return null;
    const tasks = data.tasks || {};
    const creation = data.creation || {};
    const taskLeaderboard = Array.isArray(tasks.leaderboard) ? tasks.leaderboard : [];
    const createdAdditionalServices = data.created_additional_services || [];
    const completedAdditionalServices = data.completed_additional_services || [];
    const repeatStages = data.repeat_stages || [];
    const repeatCompletedAdditionalServices = data.repeat_completed_additional_services || [];
    const showRepeatExecutions = Boolean(repeatStages.length || repeatCompletedAdditionalServices.length);
    return (
      <div className="space-y-4">
        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <SummaryCard label="Создано" value={formatMetric(data.summary?.orders_created)} note="Нажмите, чтобы открыть заказы" icon={Sparkles} tone="blue" onClick={() => openGeneralOrderDetails('created', null, 'Созданные заказы')} />
          <SummaryCard label="Завершено" value={formatMetric(data.summary?.orders_completed)} note="Нажмите, чтобы открыть заказы" icon={Target} tone="emerald" onClick={() => openGeneralOrderDetails('completed', null, 'Завершённые заказы')} />
          <SummaryCard label="Повторы" value={formatMetric(data.summary?.repeat_orders)} note="Нажмите, чтобы открыть заказы" icon={RefreshCw} tone="violet" onClick={() => openGeneralOrderDetails('repeat', null, 'Заказы с повторами')} />
          <SummaryCard label="Выручка" value={formatMoney(data.summary?.revenue_total)} note={`Открыть заказы · скидки ${formatMoney(data.summary?.discounts_total)}`} icon={WalletCards} tone="amber" onClick={() => openGeneralOrderDetails('revenue', null, 'Заказы в расчёте выручки')} />
        </section>

        <DepartmentBreakdown
          rows={data.created_departments || []}
          mode="created"
          title="Создано — по подразделениям"
          icon={Sparkles}
          tone="blue"
          defaultOpen={false}
          onRowClick={(row, locality) => openGeneralOrderDetails(
            'created_department',
            row.department_id !== null && row.department_id !== undefined ? String(row.department_id) : 'none',
            locality ? `Созданные заказы: ${row.department_name || row.name} — ${locality}` : `Созданные заказы: ${row.department_name || row.name}`,
            locality
          )}
        />

        <DepartmentBreakdown
          rows={data.departments || []}
          mode="general"
          title="Завершено — по подразделениям"
          icon={Target}
          tone="emerald"
          defaultOpen={false}
          onRowClick={(row, locality) => openGeneralOrderDetails(
            'completed_department',
            row.department_id !== null && row.department_id !== undefined ? String(row.department_id) : 'none',
            locality ? `Завершённые заказы: ${row.department_name || row.name} — ${locality}` : `Завершённые заказы: ${row.department_name || row.name}`,
            locality
          )}
        />

        <SectionCard title="Статусы заказов" icon={Box} tone="emerald" collapsible defaultOpen={false}>
          <div className="grid gap-3 sm:grid-cols-3">
            <DetailStatCard label="В работе" value={formatMetric(data.summary?.orders_in_progress)} note="Открыть созданные заказы" onClick={() => openGeneralOrderDetails('status_in_progress', null, 'Созданные заказы в работе')} />
            <DetailStatCard label="Завершено" value={formatMetric(creation.orders_completed)} note="Открыть созданные заказы" onClick={() => openGeneralOrderDetails('status_completed', null, 'Созданные завершённые заказы')} />
            <DetailStatCard label="Отменено" value={formatMetric(data.summary?.orders_cancelled)} note="Открыть созданные заказы" onClick={() => openGeneralOrderDetails('status_cancelled', null, 'Созданные отменённые заказы')} />
          </div>
        </SectionCard>

        {createdAdditionalServices.length ? (
          <SectionCard title="Допуслуги — создано" icon={Plus} tone="rose" collapsible defaultOpen={false}>
            <HorizontalBars
              rows={createdAdditionalServices.map((item) => ({ name: item.name, count: item.count }))}
              tone="rose"
              subtitle="Добавлены в заказы, созданные за выбранный период. Нажмите на услугу, чтобы открыть заказы."
              onRowClick={(row) => openGeneralOrderDetails('created_additional_service', row.name, `Добавленная допуслуга: ${row.name}`)}
            />
          </SectionCard>
        ) : null}

        {completedAdditionalServices.length ? (
          <SectionCard title="Допуслуги — выполнено" icon={Plus} tone="violet" collapsible defaultOpen={false}>
            <HorizontalBars
              rows={completedAdditionalServices.map((item) => ({ name: item.name, count: item.count }))}
              tone="violet"
              subtitle="Только первые выполнения. Нажмите на услугу, чтобы открыть заказы."
              onRowClick={openGeneralAdditionalServiceDetails}
            />
          </SectionCard>
        ) : null}

        <SectionCard title={selectedStageConfig ? `Первое выполнение: ${selectedStageConfig.label}` : 'Этапы — первое выполнение'} icon={Layers3} tone="violet" collapsible>
          {data.stages?.length ? <StageBreakdown rows={data.stages} onStageSelect={openStageDetailsModal} /> : <DataEmptyState title="Нет первых выполнений" description="В выбранном диапазоне этапы впервые не завершались." />}
        </SectionCard>

        {showRepeatExecutions ? (
          <SectionCard title="Повторные выполнения" icon={RefreshCw} tone="violet" collapsible defaultOpen={false}>
            <div className="space-y-5">
              {repeatStages.length ? (
                <StageBreakdown rows={repeatStages} onStageSelect={(stageName) => openStageDetailsModal(stageName, 'repeat')} />
              ) : null}
              {repeatCompletedAdditionalServices.length ? (
                <div className={`${repeatStages.length ? 'border-t border-gray-200 pt-4 dark:border-gray-800' : ''}`}>
                  <div className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-violet-600 dark:text-violet-300">Допуслуги в повторах</div>
                  <HorizontalBars
                    rows={repeatCompletedAdditionalServices.map((item) => ({ name: item.name, count: item.count }))}
                    tone="violet"
                    subtitle="Нажмите на услугу, чтобы открыть только повторные выполнения."
                    onRowClick={(row) => openGeneralAdditionalServiceDetails(row, 'repeat')}
                  />
                </div>
              ) : null}
            </div>
          </SectionCard>
        ) : null}

        <SectionCard title="Поручения" icon={ListTodo} tone="blue" collapsible>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl bg-gray-50 px-3 py-3 dark:bg-gray-950/40"><div className="text-xs text-gray-500 dark:text-gray-400">Создано</div><div className="mt-2 font-semibold text-gray-950 dark:text-white">{formatMetric(tasks.created_count)}</div></div>
            <div className="rounded-2xl bg-gray-50 px-3 py-3 dark:bg-gray-950/40"><div className="text-xs text-gray-500 dark:text-gray-400">Выполнено</div><div className="mt-2 font-semibold text-gray-950 dark:text-white">{formatMetric(tasks.completed_count)}</div></div>
            <div className="rounded-2xl bg-gray-50 px-3 py-3 dark:bg-gray-950/40"><div className="text-xs text-gray-500 dark:text-gray-400">Отменено</div><div className="mt-2 font-semibold text-gray-950 dark:text-white">{formatMetric(tasks.cancelled_count)}</div></div>
            <div className="rounded-2xl bg-gray-50 px-3 py-3 dark:bg-gray-950/40"><div className="text-xs text-gray-500 dark:text-gray-400">Активно сейчас</div><div className="mt-2 font-semibold text-gray-950 dark:text-white">{formatMetric(tasks.active_count)}</div></div>
          </div>
          {taskLeaderboard.length ? (
            <div className="mt-3 space-y-2">
              {taskLeaderboard.map((row) => (
                <div key={row.user_id} className="flex items-center justify-between gap-3 rounded-2xl bg-gray-50 px-3 py-3 text-sm dark:bg-gray-950/40">
                  <span className="min-w-0 truncate text-gray-700 dark:text-gray-200">{row.user_name}</span>
                  <span className="shrink-0 font-semibold text-gray-950 dark:text-white">{formatMetric(row.completed_count)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">За выбранный период ещё нет выполненных поручений.</div>
          )}
        </SectionCard>

        <SectionCard title="Тип клиента" icon={Users} tone="emerald" collapsible>
          {data.client_types?.length ? (
            <HorizontalBars
              rows={data.client_types.map((item) => ({
                name: item.label,
                value: item.client_type,
                count: item.orders_count,
                note: `Выручка: ${formatMoney(item.revenue_total)}`,
              }))}
              tone="emerald"
              onRowClick={(row) => openGeneralOrderDetails('client_type', row.value, row.name)}
            />
          ) : (
            <DataEmptyState title="Нет данных по типу клиента" description="За выбранный период в этом срезе нет завершённых заказов." />
          )}
        </SectionCard>

        <SectionCard title="Типы услуг" icon={BarChart3} tone="blue" collapsible>
          {data.service_types?.length ? (
            <HorizontalBars
              rows={data.service_types.map((item) => ({ name: item.label, value: item.service_type, count: item.orders_count, note: `Выручка: ${formatMoney(item.revenue_total)}` }))}
              tone="blue"
              onRowClick={(row) => openGeneralOrderDetails('service_type', row.value, `Тип услуги: ${row.name}`)}
            />
          ) : <DataEmptyState title="Нет данных по типам услуг" description="За выбранный период нет завершённых заказов в этом срезе." />}
        </SectionCard>

        <SectionCard title="Популярные предметы" icon={TrendingUp} tone="emerald" collapsible>
          {data.top_items?.length ? (
            <HorizontalBars
              rows={data.top_items.map((item) => ({
                name: item.name,
                count: item.quantity,
                note: `${formatMetric(item.orders_count)} зак. • ${formatMetric(item.quantity)} шт.`,
              }))}
              tone="emerald"
              subtitle="Показывает, какие предметы чаще всего встречались в завершённых заказах. Нажмите на предмет, чтобы открыть заказы."
              onRowClick={(row) => openGeneralOrderDetails('item', row.name, `Предмет: ${row.name}`)}
            />
          ) : (
            <DataEmptyState title="Нет данных по предметам" description="За выбранный период в завершённых заказах не найдено предметов для показа." />
          )}
        </SectionCard>

        <SectionCard title="Оплаты" icon={Coins} tone="amber" collapsible>
          <div className="grid gap-3 sm:grid-cols-3">
            <DetailStatCard label="Наличные" value={formatMoney(data.payment?.cash_received)} note="Открыть оплаченные заказы" onClick={() => openGeneralOrderDetails('payment', 'cash', 'Наличные оплаты')} />
            <DetailStatCard label="Безнал" value={formatMoney(data.payment?.card_received)} note="Открыть оплаченные заказы" onClick={() => openGeneralOrderDetails('payment', 'card', 'Безналичные оплаты')} />
            <DetailStatCard label="Расчётный счёт" value={formatMoney(data.payment?.settlement_received)} note="Открыть оплаченные заказы" onClick={() => openGeneralOrderDetails('payment', 'settlement', 'Оплаты на расчётный счёт')} />
          </div>
        </SectionCard>
      </div>
    );
  };

  const renderDiscountView = () => {
    if (!data) return null;
    return (
      <div className="space-y-4">
        <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <SummaryCard label="Заказы в срезе" value={formatMetric(data.summary?.orders_count)} note="База для расчёта" icon={Package} tone="blue" />
          <SummaryCard label="Со скидками" value={formatMetric(data.summary?.discounted_orders_count)} note="Хотя бы одна скидка" icon={Percent} tone="violet" />
          <SummaryCard label="Недополучено" value={formatMoney(data.summary?.discounts_total)} note="Общая сумма скидок" icon={Coins} tone="rose" />
          <SummaryCard label="Средняя скидка" value={formatMoney(data.summary?.average_discount_per_order)} note="На заказ со скидкой" icon={TrendingUp} tone="amber" />
        </section>

        <SectionCard title="Разбивка по скидкам" icon={Percent} tone="rose" collapsible>
          {data.discounts?.length ? (
            <HorizontalBars
              rows={data.discounts.map((item) => ({
                name: item.name,
                count: item.discount_total,
                note: `${formatMetric(item.orders_count)} зак. • ${formatMetric(item.share_percent)}% • ${item.source}`,
              }))}
              tone="rose"
              subtitle="Сортировка по общей сумме скидки."
            />
          ) : (
            <DataEmptyState title="Нет скидок в выбранном срезе" description="Поменяйте период или снимите фильтр этапа." />
          )}
        </SectionCard>

        <DepartmentBreakdown rows={data.departments || []} mode="discounts" />
      </div>
    );
  };

  const renderSourcesView = () => {
    if (!data) return null;
    return (
      <div className="space-y-4">
        <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <SummaryCard label="Заказы" value={formatMetric(data.summary?.orders_count)} note="В выбранном срезе" icon={Package} tone="blue" />
          <SummaryCard label="Клиенты" value={formatMetric(data.summary?.clients_count)} note="Уникальные клиенты" icon={Users} tone="emerald" />
          <SummaryCard label="Выручка" value={formatMoney(data.summary?.revenue_total)} note="По заказам среза" icon={WalletCards} tone="violet" />
          <SummaryCard label="Средний чек" value={formatMoney(data.summary?.average_check)} note="По источнику заказа" icon={Coins} tone="amber" />
        </section>

        <SectionCard title="Источники клиентов" icon={Target} tone="emerald" collapsible>
          {data.sources?.length ? (
            <HorizontalBars
              rows={data.sources.map((item) => ({
                source: item.source,
                count: item.orders_count,
                note: `${formatMetric(item.clients_count)} кл. • ${formatMetric(item.share_percent)}% • ${formatMoney(item.revenue_total)}`,
              }))}
              tone="emerald"
            />
          ) : (
            <DataEmptyState title="Нет источников в выбранном срезе" description="За этот период подходящих заказов не найдено." />
          )}
        </SectionCard>

        <DepartmentBreakdown rows={data.departments || []} mode="sources" />
      </div>
    );
  };

  const renderStageDetailsModal = () => {
    if (!stageDetailsModal) return null;
    return (
      <ModalShell
        open
        onClose={closeStageDetailsModal}
        variant="sheet"
        expandable={true}
        panelClassName="flex h-[58dvh] max-h-[58dvh] flex-col overflow-hidden rounded-t-[28px] sm:h-[58vh] sm:max-h-[58vh] sm:max-w-2xl sm:rounded-[28px]"
      >
        <div className="shrink-0 border-b border-gray-100 bg-white px-4 pt-1 pb-3 dark:border-gray-800 dark:bg-gray-900 sm:px-5 sm:pt-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-base font-semibold text-gray-950 dark:text-white">{stageDetailsModal.stage_label}</div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                {stageDetailsModal.description || 'Заказы и предметы, выполненные на этом этапе.'}
              </div>
            </div>
            <button
              type="button"
              onClick={closeStageDetailsModal}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-5">
          {stageDetailsLoading ? (
            <UsersListSkeleton count={3} />
          ) : stageDetailsError ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">{stageDetailsError}</div>
          ) : stageDetailsModal.orders?.length ? (
            <div className="space-y-3">
              {stageDetailsModal.orders.map((order) => (
                <div key={order.execution_key || order.order_id} className="rounded-2xl border border-gray-200 bg-gray-50/80 dark:border-gray-800 dark:bg-gray-950/40">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedStageOrderId((current) => (current === (order.execution_key || order.order_id) ? null : (order.execution_key || order.order_id)))}
                    onKeyDown={(event) => {
                      if (event.target !== event.currentTarget) return;
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setExpandedStageOrderId((current) => (current === (order.execution_key || order.order_id) ? null : (order.execution_key || order.order_id)));
                      }
                    }}
                    className="flex w-full items-start justify-between gap-3 p-3 text-left"
                  >
                    <div className="min-w-0">
                      <Link
                        to={`/order/${order.order_id}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          closeStageDetailsModal();
                        }}
                        className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2 py-1 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-900/50"
                        title="Открыть заказ"
                      >
                        <span>Заказ {order.order_number}</span>
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Link>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {order.completed_at ? new Date(order.completed_at).toLocaleString('ru-RU') : 'Дата не указана'}
                      </div>
                      {order.cycle_label ? (
                        <div className={`mt-1 text-xs font-medium ${order.cycle_type === 'revision' ? 'text-violet-600 dark:text-violet-300' : 'text-gray-600 dark:text-gray-300'}`}>
                          {order.cycle_label}
                        </div>
                      ) : null}
                      {(Number(order.floors_count) || 0) > 0 ? (
                        <div className="mt-2 inline-flex items-center rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-gray-600 ring-1 ring-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:ring-gray-700">
                          Этажи: {formatMetric(order.floors_count)}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <div className="rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-gray-600 ring-1 ring-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:ring-gray-700">
                        {formatMetric(order.items?.length || 0)} поз.
                        {(order.order_services || []).length ? ` · ${formatMetric(order.order_services.length)} доп.` : ''}
                      </div>
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform dark:text-gray-500 ${expandedStageOrderId === (order.execution_key || order.order_id) ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                  {expandedStageOrderId === (order.execution_key || order.order_id) ? (
                    <div className="px-3 pb-3">
                      <div className="space-y-2 border-t border-gray-200 pt-3 dark:border-gray-800">
                        {(order.items || []).map((item) => {
                          const additionalServices = Array.isArray(item.additional_services) ? item.additional_services : [];
                          return (
                            <div key={`${order.execution_key || order.order_id}-${item.item_id}`} className="rounded-2xl bg-white px-3 py-3 dark:bg-gray-900">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{item.name}</div>
                                  <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400">
                                    <span>Кол-во: {formatMetric(item.quantity)}</span>
                                    {(Number(item.area) || 0) > 0 ? <span>Площадь: {formatMetric(item.area)} м²</span> : null}
                                    {(Number(item.weight) || 0) > 0 ? <span>Вес: {formatMetric(item.weight)} кг</span> : null}
                                  </div>
                                </div>
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-300">
                                  <Box className="h-4 w-4" />
                                </div>
                              </div>
                              {additionalServices.length ? (
                                <details className="group mt-2 rounded-xl bg-violet-50/80 dark:bg-violet-950/20">
                                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2 text-xs font-medium text-violet-700 dark:text-violet-300">
                                    <span>Доп. услуги предмета · {formatMetric(additionalServices.length)}</span>
                                    <ChevronDown className="h-3.5 w-3.5 shrink-0 transition-transform group-open:rotate-180" />
                                  </summary>
                                  <div className="space-y-1 border-t border-violet-100 px-3 py-2 dark:border-violet-900/50">
                                    {additionalServices.map((service, serviceIndex) => {
                                      const price = Number(service.price) || 0;
                                      const priceLabel = price > 0
                                        ? (service.unit_type === 'percent' ? `+${formatMetric(price)}%` : `+${formatMoney(price)}`)
                                        : '';
                                      return (
                                        <div key={`${service.id || service.name}-${serviceIndex}`} className="flex items-start justify-between gap-3 text-xs">
                                          <span className="min-w-0 text-gray-700 dark:text-gray-200">{service.name}</span>
                                          {priceLabel ? <span className="shrink-0 font-medium text-violet-700 dark:text-violet-300">{priceLabel}</span> : null}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </details>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <DataEmptyState title="Нет деталей по этапу" description="За выбранный период на этом этапе ничего не найдено." />
          )}
        </div>
      </ModalShell>
    );
  };

  const renderOrderDetailsModal = () => {
    if (!orderDetailsModal) return null;
    const isFinanceMode = orderDetailsModal.mode === 'finance';
    const dateKind = orderDetailsModal.date_kind || (isFinanceMode ? 'completed' : 'created');
    const showFinance = isFinanceMode || Boolean(orderDetailsModal.show_finance);
    const formatOrderDate = (order) => {
      if (dateKind === 'revision') {
        return order.event_at ? `Повтор открыт: ${new Date(order.event_at).toLocaleString('ru-RU')}` : 'Дата повтора не указана';
      }
      if (dateKind === 'completed') {
        return order.completed_at ? `Завершён: ${new Date(order.completed_at).toLocaleString('ru-RU')}` : 'Дата завершения не указана';
      }
      return order.created_at ? `Создан: ${new Date(order.created_at).toLocaleString('ru-RU')}` : 'Дата создания не указана';
    };
    return (
      <ModalShell
        open
        onClose={closeOrderDetailsModal}
        variant="sheet"
        expandable={true}
        panelClassName="flex h-[58dvh] max-h-[58dvh] flex-col overflow-hidden rounded-t-[28px] sm:h-[58vh] sm:max-h-[58vh] sm:max-w-2xl sm:rounded-[28px]"
      >
        <div className="shrink-0 border-b border-gray-100 bg-white px-4 pt-1 pb-3 dark:border-gray-800 dark:bg-gray-900 sm:px-5 sm:pt-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-base font-semibold text-gray-950 dark:text-white">{orderDetailsModal.title}</div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                {orderDetailsModal.description || (isFinanceMode ? 'Показывает оплаченные закрытые заказы этого сотрудника.' : 'Показывает заказы, которые сотрудник создал за выбранный период.')}
              </div>
            </div>
            <button
              type="button"
              onClick={closeOrderDetailsModal}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {orderDetailsDepartmentOptions.length > 1 ? (
            <div className="mt-2.5 flex flex-wrap gap-1.5 border-t border-gray-100 pt-2.5 dark:border-gray-800">
              <button
                type="button"
                onClick={() => {
                  haptic('selection');
                  setSelectedOrderDetailsDept('all');
                }}
                className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                  selectedOrderDetailsDept === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                Все подразделения ({modalOrders.length})
              </button>
              {orderDetailsDepartmentOptions.map((dept) => (
                <button
                  key={dept.key}
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setSelectedOrderDetailsDept(dept.key);
                  }}
                  className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                    selectedOrderDetailsDept === dept.key
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  {dept.name} ({dept.count})
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-5">

          {orderDetailsLoading ? (
            <UsersListSkeleton count={3} />
          ) : orderDetailsError ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">{orderDetailsError}</div>
          ) : displayedModalOrders.length ? (
            <div className="space-y-3">
              {displayedModalOrders.map((order) => (
                <div key={order.order_id} className="rounded-2xl border border-gray-200 bg-gray-50/80 dark:border-gray-800 dark:bg-gray-950/40">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedDetailsOrderId((current) => (current === order.order_id ? null : order.order_id))}
                    onKeyDown={(event) => {
                      if (event.target !== event.currentTarget) return;
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setExpandedDetailsOrderId((current) => (current === order.order_id ? null : order.order_id));
                      }
                    }}
                    className="flex w-full items-start justify-between gap-3 p-3 text-left"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          to={`/order/${order.order_id}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            closeOrderDetailsModal();
                          }}
                          className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2 py-1 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-900/50"
                          title="Открыть заказ"
                        >
                          <span>Заказ {order.order_number}</span>
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                        {order.department_name ? (
                          <span className="inline-flex items-center gap-1 rounded-md bg-blue-50/80 px-2 py-0.5 text-[11px] font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20 dark:bg-blue-950/40 dark:text-blue-300 dark:ring-blue-500/30">
                            <Building2 className="h-3 w-3" />
                            {order.department_name}
                          </span>
                        ) : null}
                        {order.locality ? (
                          (() => {
                            const isSelfPickup = String(order.locality || '').trim().toLowerCase() === 'самовывоз';
                            return (
                              <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50/80 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-500/30">
                                {isSelfPickup ? (
                                  <UserCheck className="h-3 w-3 text-violet-600 dark:text-violet-400" />
                                ) : (
                                  <MapPin className="h-3 w-3" />
                                )}
                                {order.locality}
                              </span>
                            );
                          })()
                        ) : null}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {formatOrderDate(order)}
                      </div>
                      {order.address ? (
                        <div className="mt-1 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <MapPin className="h-3 w-3 shrink-0 text-gray-400" />
                          <span className="truncate">{order.address}</span>
                        </div>
                      ) : null}
                      {(Number(order.match_count) || 0) > 1 ? (
                        <div className="mt-1 text-xs font-medium text-violet-600 dark:text-violet-300">Повторов за период: {formatMetric(order.match_count)}</div>
                      ) : null}
                      {showFinance ? (
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400">
                          <span>Сумма: {formatMoney(order.final_amount)}</span>
                          {order.payment_method ? (
                            <span>Оплата: {formatPaymentMethod(order.payment_method)}</span>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <div className="rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-gray-600 ring-1 ring-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:ring-gray-700">
                        {formatMetric(order.items?.length || 0)} поз.
                      </div>
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform dark:text-gray-500 ${expandedDetailsOrderId === order.order_id ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                  {expandedDetailsOrderId === order.order_id ? (
                    <div className="px-3 pb-3">
                      <div className="space-y-2 border-t border-gray-200 pt-3 dark:border-gray-800">
                        {(order.items || []).length ? (
                          (order.items || []).map((item) => {
                            const additionalServices = Array.isArray(item.additional_services) ? item.additional_services : [];
                            return (
                              <div key={`${order.order_id}-${item.item_id}`} className="rounded-2xl bg-white px-3 py-3 dark:bg-gray-900">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{item.name}</div>
                                    <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400">
                                      <span>Кол-во: {formatMetric(item.quantity)}</span>
                                      {(Number(item.area) || 0) > 0 ? <span>Площадь: {formatMetric(item.area)} м²</span> : null}
                                      {(Number(item.weight) || 0) > 0 ? <span>Вес: {formatMetric(item.weight)} кг</span> : null}
                                    </div>
                                  </div>
                                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-300">
                                    <Box className="h-4 w-4" />
                                  </div>
                                </div>
                                {additionalServices.length ? (
                                  <details className="group mt-2 rounded-xl bg-violet-50/80 dark:bg-violet-950/20">
                                    <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2 text-xs font-medium text-violet-700 dark:text-violet-300">
                                      <span>Доп. услуги предмета · {formatMetric(additionalServices.length)}</span>
                                      <ChevronDown className="h-3.5 w-3.5 shrink-0 transition-transform group-open:rotate-180" />
                                    </summary>
                                    <div className="space-y-1 border-t border-violet-100 px-3 py-2 dark:border-violet-900/50">
                                      {additionalServices.map((service, serviceIndex) => (
                                        <div key={`${service.id || service.name}-${serviceIndex}`} className="flex items-start justify-between gap-3 text-xs">
                                          <span className="min-w-0 text-gray-700 dark:text-gray-200">{service.name}</span>
                                          {(Number(service.price) || 0) > 0 ? (
                                            <span className="shrink-0 font-medium text-violet-700 dark:text-violet-300">
                                              {service.unit_type === 'percent' ? `+${formatMetric(service.price)}%` : `+${formatMoney(service.price)}`}
                                            </span>
                                          ) : null}
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                ) : null}
                              </div>
                            );
                          })
                        ) : !(order.order_services || []).length ? (
                          <DataEmptyState title="Нет предметов" description="У этого заказа нет основных позиций для показа." />
                        ) : null}
                        {(order.order_services || []).length ? (
                          <details className="group rounded-2xl bg-rose-50/80 dark:bg-rose-950/20">
                            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-3 text-sm font-medium text-rose-700 dark:text-rose-300">
                              <span>Доп. услуги заказа · {formatMetric(order.order_services.length)}</span>
                              <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" />
                            </summary>
                            <div className="space-y-2 border-t border-rose-100 px-3 py-3 dark:border-rose-900/50">
                              {order.order_services.map((service) => (
                                <div key={`${order.order_id}-service-${service.item_id}`} className="flex items-start justify-between gap-3 text-xs">
                                  <div className="min-w-0">
                                    <div className="text-gray-700 dark:text-gray-200">{service.name}</div>
                                    <div className="mt-0.5 text-gray-500 dark:text-gray-400">Кол-во: {formatMetric(service.quantity)}</div>
                                  </div>
                                  {(Number(service.subtotal) || Number(service.price) || 0) > 0 ? (
                                    <span className="shrink-0 font-medium text-rose-700 dark:text-rose-300">{formatMoney(service.subtotal || service.price)}</span>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          </details>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <DataEmptyState
              title={modalOrders.length ? 'Нет заказов в выбранном подразделении' : 'Нет заказов'}
              description={modalOrders.length ? 'В этом подразделении нет заказов за выбранный период.' : 'За выбранный период подходящих заказов не найдено.'}
            />
          )}
        </div>
      </ModalShell>
    );
  };

  const scopeHint = getScopeHint(activeTab, draftFilters.stage);
  const draftPeriodDays = useMemo(
    () => getPeriodDays(draftFilters.date_from, draftFilters.date_to),
    [draftFilters.date_from, draftFilters.date_to]
  );
  const appliedPeriodDays = useMemo(
    () => getPeriodDays(appliedFilters.date_from, appliedFilters.date_to),
    [appliedFilters.date_from, appliedFilters.date_to]
  );
  const isPeriodInvalid = draftPeriodDays < 0;
  const isPeriodTooLong = draftPeriodDays > MAX_STATISTICS_DAYS;
  const periodValidationError = useMemo(() => {
    if (isPeriodInvalid) return 'Дата начала не может быть позже даты окончания';
    if (isPeriodTooLong) return `Максимальный период — ${MAX_STATISTICS_DAYS} дня (выбрано: ${draftPeriodDays} дн.)`;
    return '';
  }, [isPeriodInvalid, isPeriodTooLong, draftPeriodDays]);

  const canApplyFilters = !(
    loading
    || isPeriodInvalid
    || isPeriodTooLong
    || (isAdminLike && activeTab === 'employee' && !draftFilters.user_id)
    || (!isAdminLike && activeTab === 'employee' && !draftFilters.user_id)
    || !draftFilters.date_from
    || !draftFilters.date_to
    || !hasDraftChanges
  );
  const AppliedStageIcon = appliedStageConfig?.icon;
  const selectedStageConfig = appliedFilters.stage ? getStageConfig(appliedFilters.stage) : null;
  const selectedUser = isAdminLike
    ? users.find((user) => String(user.id) === String(appliedFilters.user_id || ''))
    : (currentAuthUser || data?.user || (resolvedUserId ? { id: resolvedUserId } : null));
  const selectedUserLabel = selectedUser ? formatStatisticsUserLabel(selectedUser) : 'Не выбран';
  const showStatisticsError = Boolean(error && !data && !loading);
  const isActualOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const isOfflineStatisticsError = showStatisticsError && isActualOffline;

  useEffect(() => {
    if (error) showToast(error, 'error');
  }, [error, showToast]);

  useEffect(() => {
    if (stageDetailsError) showToast(stageDetailsError, 'error');
  }, [stageDetailsError, showToast]);

  useEffect(() => {
    if (orderDetailsError) showToast(orderDetailsError, 'error');
  }, [orderDetailsError, showToast]);

  return (
    <div className="space-y-4 animate-fade-in desktop-content-container-wide">
      {isAdminLike ? (
        <>
          <section className="rounded-2xl border border-gray-200 bg-white p-3.5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">Раздел статистики</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {tabs.map((tab) => (
                <TabButton
                  key={tab.id}
                  active={activeTab === tab.id}
                  label={tab.label}
                  icon={tab.icon}
                  iconOnly
                  onClick={() => {
                    haptic('selection');
                    setActiveTab(tab.id);
                  }}
                />
              ))}
            </div>
          </section>

          <h1 aria-live="polite" className="px-1 text-xl font-semibold tracking-tight text-gray-950 dark:text-white">
            {STATISTICS_SECTION_TITLES[activeTab] || 'Статистика'}
          </h1>

          <section className="relative rounded-2xl border border-gray-200 bg-white p-3.5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">Период</div>
                <div className="mt-1 text-sm font-medium text-gray-950 dark:text-white">{formatFilterRange(appliedFilters.date_from, appliedFilters.date_to)}</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {activeTab === 'employee' ? (
                    <div className="inline-flex items-center rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-gray-600 ring-1 ring-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:ring-gray-700">
                      {selectedUserLabel}
                    </div>
                  ) : null}
                  {appliedStageConfig && AppliedStageIcon ? (
                    <div className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-normal ${STAGE_BADGE_STYLES[appliedStageConfig.color] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200'}`}>
                      <AppliedStageIcon className="h-3.5 w-3.5" />
                      {appliedStageConfig.label}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    haptic('tap');
                    setShowCompactFilters((prev) => !prev);
                  }}
                  aria-label="Открыть фильтры"
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  <Filter className="h-4 w-4" />
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showCompactFilters ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>

            <AnimatePresence initial={false}>
              {showCompactFilters ? (
                <motion.div
                  initial={{ opacity: 0, height: 0, marginTop: 0 }}
                  animate={{ opacity: 1, height: 'auto', marginTop: 12 }}
                  exit={{ opacity: 0, height: 0, marginTop: 0 }}
                  transition={{ duration: 0.18 }}
                  className="relative z-20"
                >
                  <div className="space-y-3 border-t border-gray-100 pt-3 dark:border-gray-800">
                    <div className="grid gap-3 lg:grid-cols-4">
                      {activeTab === 'employee' ? (
                        <div className="lg:col-span-1">
                          <Dropdown
                            label="Пользователь"
                            value={draftFilters.user_id}
                            options={userOptions}
                            onChange={(value) => setDraftFilters((prev) => ({ ...prev, user_id: value }))}
                            placeholder={loadingUsers ? 'Загрузка пользователей...' : 'Выберите пользователя'}
                            menuLabel="Сотрудники"
                            disabled={loadingUsers}
                            buttonClassName="!py-2.5 !text-sm !font-normal !border-gray-200 dark:!border-gray-700 !focus:ring-gray-200 dark:!focus:ring-gray-700"
                            menuClassName="!mt-2 !rounded-2xl !border-gray-200 dark:!border-gray-700 !shadow-xl"
                            menuHeaderClassName="!bg-white dark:!bg-gray-800 !text-gray-500 dark:!text-gray-400"
                          />
                        </div>
                      ) : null}

                      <div className={activeTab === 'employee' ? 'lg:col-span-1' : 'lg:col-span-2'}>
                        <Dropdown
                          label="Этап"
                          value={draftFilters.stage}
                          options={stageOptions}
                          onChange={(value) => setDraftFilters((prev) => ({ ...prev, stage: value }))}
                          placeholder="Все этапы"
                          menuLabel="Этапы"
                          buttonClassName="!py-2.5 !text-sm !font-normal !border-gray-200 dark:!border-gray-700 !focus:ring-gray-200 dark:!focus:ring-gray-700"
                          menuClassName="!mt-2 !rounded-2xl !border-gray-200 dark:!border-gray-700 !shadow-xl"
                          menuHeaderClassName="!bg-white dark:!bg-gray-800 !text-gray-500 dark:!text-gray-400"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2 lg:col-span-2">
                        <div>
                          <DatePicker value={draftFilters.date_from} onChange={(value) => setDraftFilters((prev) => ({ ...prev, date_from: value }))} placeholder="Дата от" allowPastDates inputClassName="!text-sm !font-normal !tracking-normal !pl-8 !pr-7 !py-2.5 !rounded-xl !text-left" />
                        </div>
                        <div>
                          <DatePicker value={draftFilters.date_to} onChange={(value) => setDraftFilters((prev) => ({ ...prev, date_to: value }))} placeholder="Дата до" allowPastDates inputClassName="!text-sm !font-normal !tracking-normal !pl-8 !pr-7 !py-2.5 !rounded-xl !text-left" />
                        </div>
                      </div>
                      {periodValidationError ? (
                        <div className="col-span-full -mt-1 text-xs font-medium text-rose-500 dark:text-rose-400">
                          {periodValidationError}
                        </div>
                      ) : draftPeriodDays > 30 ? (
                        <div className="col-span-full -mt-1 flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                          <Clock3 className="h-3.5 w-3.5 shrink-0" />
                          <span>Период более 30 дней ({draftPeriodDays} дн.). Расчёт может занять чуть больше времени.</span>
                        </div>
                      ) : null}
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                      <div className="flex flex-wrap gap-2">
                        <QuickRangeButton active={periodPreset === '7'} label="7 дней" onClick={() => setPresetRange('7')} />
                        <QuickRangeButton active={periodPreset === '30'} label="30 дней" onClick={() => setPresetRange('30')} />
                        <QuickRangeButton active={periodPreset === 'month'} label="Текущий месяц" onClick={() => setPresetRange('month')} />
                      </div>

                      <div className="flex items-center gap-2 sm:ml-auto">
                        <button type="button" onClick={resetFilters} className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 lg:px-4 lg:py-2.5">
                          <RefreshCw className="h-4 w-4" />
                          Сброс
                        </button>
                        <button
                          type="button"
                          onClick={applyFilters}
                          disabled={!canApplyFilters}
                          className="inline-flex flex-1 items-center justify-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300 dark:disabled:bg-blue-900/50 sm:flex-initial lg:px-6 lg:py-2.5"
                        >
                          Показать
                        </button>
                      </div>
                    </div>

                    <div className="text-xs leading-5 text-gray-500 dark:text-gray-400">
                      {scopeHint || 'Фильтры применяются к активной вкладке. Для сотрудника доступна только собственная статистика.'}
                    </div>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </section>
        </>
      ) : (
        <section className="relative rounded-2xl border border-gray-200 bg-white p-3.5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">Период</div>
              <div className="mt-1 text-sm font-medium text-gray-950 dark:text-white">{formatFilterRange(appliedFilters.date_from, appliedFilters.date_to)}</div>
              <div className="mt-2 flex flex-wrap gap-2">
                <div className="inline-flex items-center rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-gray-600 ring-1 ring-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:ring-gray-700">
                  {selectedUserLabel}
                </div>
              {appliedStageConfig && AppliedStageIcon ? (
                <div className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-normal ${STAGE_BADGE_STYLES[appliedStageConfig.color] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200'}`}>
                  <AppliedStageIcon className="h-3.5 w-3.5" />
                  {appliedStageConfig.label}
                </div>
              ) : null}
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                haptic('tap');
                setShowCompactFilters((prev) => !prev);
              }}
              aria-label="Открыть фильтры"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              <Filter className="h-4 w-4" />
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showCompactFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          <AnimatePresence initial={false}>
            {showCompactFilters ? (
              <motion.div
                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                animate={{ opacity: 1, height: 'auto', marginTop: 12 }}
                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                transition={{ duration: 0.18 }}
                className="relative z-20"
              >
                <div className="space-y-3 border-t border-gray-100 pt-3 dark:border-gray-800">
                  <div className="grid gap-3 lg:grid-cols-4">
                    <div className="lg:col-span-1">
                      <Dropdown
                        label="Пользователь"
                        value={draftFilters.user_id}
                        options={fixedUserOptions}
                        onChange={() => {}}
                        placeholder={selfUserOption ? selfUserOption.label : 'Определяем пользователя...'}
                        menuLabel="Пользователь"
                        disabled
                        buttonClassName="!py-2.5 !text-sm !font-normal !border-gray-200 dark:!border-gray-700 !bg-gray-50 dark:!bg-gray-800 !text-gray-500 dark:!text-gray-400"
                        menuClassName="!mt-2 !rounded-2xl !border-gray-200 dark:!border-gray-700 !shadow-xl"
                        menuHeaderClassName="!bg-white dark:!bg-gray-800 !text-gray-500 dark:!text-gray-400"
                      />
                    </div>

                    <div className="lg:col-span-1">
                      <Dropdown
                        label="Этап"
                        value={draftFilters.stage}
                        options={stageOptions}
                        onChange={(value) => setDraftFilters((prev) => ({ ...prev, stage: value }))}
                        placeholder="Все этапы"
                        menuLabel="Этапы"
                        buttonClassName="!py-2.5 !text-sm !font-normal !border-gray-200 dark:!border-gray-700 !focus:ring-gray-200 dark:!focus:ring-gray-700"
                        menuClassName="!mt-2 !rounded-2xl !border-gray-200 dark:!border-gray-700 !shadow-xl"
                        menuHeaderClassName="!bg-white dark:!bg-gray-800 !text-gray-500 dark:!text-gray-400"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2 lg:col-span-2">
                      <div>
                        <DatePicker value={draftFilters.date_from} onChange={(value) => setDraftFilters((prev) => ({ ...prev, date_from: value }))} placeholder="Дата от" allowPastDates inputClassName="!text-sm !font-normal !tracking-normal !pl-8 !pr-7 !py-2.5 !rounded-xl !text-left" />
                      </div>
                      <div>
                        <DatePicker value={draftFilters.date_to} onChange={(value) => setDraftFilters((prev) => ({ ...prev, date_to: value }))} placeholder="Дата до" allowPastDates inputClassName="!text-sm !font-normal !tracking-normal !pl-8 !pr-7 !py-2.5 !rounded-xl !text-left" />
                      </div>
                    </div>
                    {periodValidationError ? (
                      <div className="col-span-full -mt-1 text-xs font-medium text-rose-500 dark:text-rose-400">
                        {periodValidationError}
                      </div>
                    ) : draftPeriodDays > 30 ? (
                      <div className="col-span-full -mt-1 flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                        <Clock3 className="h-3.5 w-3.5 shrink-0" />
                        <span>Период более 30 дней ({draftPeriodDays} дн.). Расчёт может занять чуть больше времени.</span>
                      </div>
                    ) : null}
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                    <div className="flex flex-wrap gap-2">
                      <QuickRangeButton active={periodPreset === '7'} label="7 дней" onClick={() => setPresetRange('7')} />
                      <QuickRangeButton active={periodPreset === '30'} label="30 дней" onClick={() => setPresetRange('30')} />
                      <QuickRangeButton active={periodPreset === 'month'} label="Текущий месяц" onClick={() => setPresetRange('month')} />
                    </div>

                    <div className="flex items-center gap-2 sm:ml-auto">
                      <button type="button" onClick={resetFilters} className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 lg:px-4 lg:py-2.5">
                        <RefreshCw className="h-4 w-4" />
                        Сброс
                      </button>
                      <button
                        type="button"
                        onClick={applyFilters}
                        disabled={!canApplyFilters}
                        className="inline-flex flex-1 items-center justify-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300 dark:disabled:bg-blue-900/50 sm:flex-initial lg:px-6 lg:py-2.5"
                      >
                        Показать
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </section>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={`${activeTab}-${appliedFilters.date_from}-${appliedFilters.date_to}-${appliedFilters.stage}-${appliedFilters.user_id}`}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18 }}
        >
          {loading ? (
            <StatisticsSkeleton
              isLongPeriod={appliedPeriodDays > 30}
              periodDays={appliedPeriodDays}
            />
          ) : showStatisticsError ? (
            <StatisticsErrorState
              title={isOfflineStatisticsError ? 'Статистика недоступна офлайн' : 'Не удалось загрузить статистику'}
              description={
                isOfflineStatisticsError
                  ? 'Для статистики нужны свежие данные с сервера. Когда связь вернётся, раздел попробует обновиться автоматически.'
                  : error
              }
              onRetry={loadStatistics}
              retryDisabled={loading}
            />
          ) : activeTab === 'employee' ? (
            renderEmployeeView()
          ) : activeTab === 'general' ? (
            renderGeneralView()
          ) : activeTab === 'discounts' ? (
            renderDiscountView()
          ) : (
            renderSourcesView()
          )}
        </motion.div>
      </AnimatePresence>
      {renderStageDetailsModal()}
      {renderOrderDetailsModal()}
          </div>
  );
};

export default Statistics;
